import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from "react";
import { ChatRequest } from "../../apiClient/data-contracts";
import { API_BASE_URL } from "../../config";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ChevronRight,
  CornerDownRight,
  Copy,
  Target,
  Clock,
  ArrowRight,
  FileText,
  Bot,
  ThumbsUp,
  ThumbsDown,
  User,
  Paperclip,
  X,
  Info,
  Trash,
  Star,
  Search,
  FileSearch,
  Loader2,
  FileDown,
  Download,
  Gavel,
  FolderOpen,
  Sparkles,
  Zap,
  MessageSquare,
  PenLine,
  SquarePen,
  Pin,
  Plus,
  Lightbulb,
  Glasses,
  ClipboardList,
  Combine,
  LayoutList,
  Share2,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  AlertCircle,
  BookOpen,
  Check,
  AlertTriangle,
  ArrowLeft,
  History
} from "lucide-react";
import { fetchScrapperProcesos, ScrapperProceso } from "@/services/scraperApi";
import { jsPDF } from "jspdf";
import { asBlob } from "html-docx-js-typescript";
import { saveAs } from "file-saver";
import { marked } from "marked";
import LegalDisclaimer from "@/components/LegalDisclaimer";

marked.setOptions({ gfm: true, breaks: true });
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import DashboardBreadcrumbs from "@/components/ui/DashboardBreadcrumbs";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useApiClient } from "@/hooks/use-api-client";
import { friendUsageGuard } from "@/lib/services/friend-usage-guard";
import { UsageTracker } from "@/lib/services/usage-tracker";
import { useAgentExecution, Message, TraceStep } from "@/contexts/AgentExecutionContext";
import { useDocumentViewer } from "@/contexts/DocumentViewerContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFlowEngine } from "@/hooks/useFlowEngine";
import { useIsMobile } from "@/hooks/use-mobile";
import { getAgentBySlug } from "@/lib/agents";
import { cn } from "@/lib/utils";

// Supabase Storage config
const STORAGE_BUCKET = "legal-chatbot";

interface ChatResponseContent {
  content: string;
  recommendations?: string[];
  contextual_questions?: string[];
  relevant_laws?: string[];
  sarai_classification?: Record<string, string>;
  sub_queries?: Array<{
    id?: string;
    collection: string;
    query: string;
  }>;
  sources?: string[];
  citations?: string[];
}

interface Highlight {
  id?: string;
  highlighted_text?: string;
  text?: string;
  color: string;
  message_index?: number;
}

const HIGHLIGHT_COLOR_MAP: Record<string, string> = {
  yellow: "#fef08a",
  green: "#bbf7d0",
  blue: "#bfdbfe",
  pink: "#fbcfe8",
};

const HIGHLIGHT_PALETTE = [
  { name: "yellow", value: "#fef08a", label: "Amarillo" },
  { name: "green", value: "#bbf7d0", label: "Verde" },
  { name: "blue", value: "#bfdbfe", label: "Azul" },
  { name: "pink", value: "#fbcfe8", label: "Rosa" },
] as const;

function buildHighlightTextIndex(root: HTMLElement): {
  flat: string;
  segments: { node: Text; start: number }[];
} {
  const segments: { node: Text; start: number }[] = [];
  let flat = "";
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  let w: Node | null;
  while ((w = walker.nextNode())) {
    const t = w as Text;
    const el = t.parentElement;
    if (!el || el.closest("pre, code, mark")) continue;
    segments.push({ node: t, start: flat.length });
    flat += t.textContent || "";
  }
  return { flat, segments };
}

function applyHighlightAcrossNodes(
  segments: { node: Text; start: number }[],
  rangeStart: number,
  rangeEnd: number,
  bg: string
): void {
  const parts: { node: Text; cutStart: number; cutEnd: number }[] = [];
  for (const seg of segments) {
    const text = seg.node.textContent || "";
    const nodeStart = seg.start;
    const nodeEnd = nodeStart + text.length;
    if (nodeEnd <= rangeStart || nodeStart >= rangeEnd) continue;
    const cutStart = Math.max(rangeStart, nodeStart) - nodeStart;
    const cutEnd = Math.min(rangeEnd, nodeEnd) - nodeStart;
    if (cutStart < cutEnd) parts.push({ node: seg.node, cutStart, cutEnd });
  }
  for (const { node, cutStart, cutEnd } of parts) {
    const text = node.textContent || "";
    const before = text.slice(0, cutStart);
    const middle = text.slice(cutStart, cutEnd);
    const after = text.slice(cutEnd);
    const parent = node.parentNode;
    if (!parent || !middle) continue;
    const frag = document.createDocumentFragment();
    if (before) frag.appendChild(document.createTextNode(before));
    const markEl = document.createElement("mark");
    markEl.style.backgroundColor = bg;
    markEl.style.borderRadius = "2px";
    markEl.setAttribute("data-chat-highlight", "1");
    markEl.appendChild(document.createTextNode(middle));
    frag.appendChild(markEl);
    if (after) frag.appendChild(document.createTextNode(after));
    parent.replaceChild(frag, node);
  }
}

/** Wrap every case-insensitive occurrence of needle (may span bold/links/lists; skip pre/code/existing marks). */
function wrapTextOccurrencesForHighlight(root: HTMLElement, needle: string, bg: string): void {
  const needleLower = needle.toLowerCase().trim();
  if (!needleLower) return;
  while (true) {
    const { flat, segments } = buildHighlightTextIndex(root);
    const idx = flat.toLowerCase().indexOf(needleLower);
    if (idx === -1) break;
    applyHighlightAcrossNodes(segments, idx, idx + needleLower.length, bg);
  }
}

function escapeHtmlPlain(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function applyHighlightsToRenderedHtml(html: string, highlights: Highlight[]): string {
  if (!html || !highlights?.length) return html;
  if (typeof document === "undefined") return html;
  const sorted = [...highlights].sort(
    (a, b) =>
      (b.highlighted_text || b.text || "").length -
      (a.highlighted_text || a.text || "").length
  );
  const container = document.createElement("div");
  container.innerHTML = html;
  for (const h of sorted) {
    const needle = (h.highlighted_text || h.text || "").trim();
    if (!needle) continue;
    const bg = HIGHLIGHT_COLOR_MAP[h.color] || HIGHLIGHT_COLOR_MAP.yellow;
    wrapTextOccurrencesForHighlight(container, needle, bg);
  }
  return container.innerHTML;
}

const SelectionToolbar = ({
  rect,
  toolbarLeft,
  toolbarTop,
  onSelectColor,
  onContinue,
  onClose,
}: {
  rect: DOMRect;
  toolbarLeft: number;
  toolbarTop: number;
  onSelectColor: (color: string) => void;
  onContinue: () => void;
  onClose: () => void;
}) => {
  const approximateHeight = 100;
  const margin = 8;
  let top = toolbarTop - approximateHeight - margin;
  if (top < margin) {
    top = rect.bottom + margin;
  }
  const maxTop =
    typeof window !== "undefined"
      ? window.innerHeight - approximateHeight - margin
      : top;
  top = Math.min(maxTop, Math.max(margin, top));
  let left = toolbarLeft;
  if (typeof window !== "undefined") {
    left = Math.min(window.innerWidth - margin, Math.max(margin, left));
  }

  return (
    <div
      className="fixed z-[9999] flex flex-col gap-2 rounded-xl shadow-2xl border border-slate-200 bg-white p-2 animate-in fade-in zoom-in-95 duration-200 select-none"
      style={{
        left,
        top,
        transform: "translateX(-50%)",
      }}
    >
      <div className="flex items-center gap-2 justify-center flex-wrap">
        {HIGHLIGHT_PALETTE.map((c) => (
          <button
            key={c.name}
            type="button"
            onClick={() => onSelectColor(c.name)}
            className="w-7 h-7 rounded-full border border-slate-200 hover:scale-110 transition-transform shadow-sm"
            style={{ backgroundColor: c.value }}
            title={c.label}
          />
        ))}
        <div className="w-px h-4 bg-slate-200 self-center mx-0.5" />
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded text-slate-400 hover:text-slate-600"
          aria-label="Cerrar"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <button
        type="button"
        onClick={onContinue}
        className="flex items-center justify-center gap-2 w-full px-3 py-2 rounded-lg bg-slate-800 text-white text-xs font-bold hover:bg-slate-700 transition-colors border border-white/10"
      >
        <MessageSquare className="h-3.5 w-3.5" />
        Continuar en el chat
      </button>
    </div>
  );
};

type APIResponseContent = Record<string, string | string[] | undefined>;

interface ChatResponse {
  response: APIResponseContent;
  conversation_id: string;
  references?: {
    sources?: string[];
    citations?: string[];
  };
}

// Local Message interface removed as it conflicts with import from contexts/AgentExecutionContext
interface Recommendation {
  label: string;
  prompt: string;
}

interface NextBestActionsProps {
  actions: Recommendation[];
  onSelect: (prompt: string) => void;
}

const NextBestActions = ({ actions, onSelect }: NextBestActionsProps) => (
  <div className="mt-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
    <div className="flex items-center gap-2 mb-3">
      <div className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-600">
        <Sparkles className="h-3 w-3" />
      </div>
      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.15em]">
        Siguiente Acción Sugerida
      </span>
    </div>
    <div className="flex flex-wrap gap-2.5">
      {actions.map((action, idx) => (
        <button
          key={idx}
          onClick={() => onSelect(action.prompt)}
          className="group flex items-center gap-2.5 px-4 py-2.5 rounded-xl border border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/50 hover:shadow-sm transition-all duration-200 text-left"
        >
          <span className="text-sm font-medium text-slate-700 group-hover:text-blue-700 transition-colors">
            {action.label}
          </span>
          <ArrowRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-blue-500 group-hover:translate-x-0.5 transition-all" />
        </button>
      ))}
    </div>
  </div>
);

const formatTimestamp = (ts?: string) => {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return ts;
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
  } catch {
    return ts;
  }
};

const SourcesList = ({
  sources,
  citations,
  sourceDetails,
  msgMetadata,
}: {
  sources?: string[];
  citations?: (string | undefined)[];
  sourceDetails?: { name: string; url: string; excerpt: string; index?: number; highlight_text?: string; article?: string; raw_url?: string; pageNumber?: number; is_canonical?: boolean; vigencia?: string; retrieval_evidence?: any; part_type?: string }[];
  msgMetadata?: any;
}) => {
  const { openDocument } = useDocumentViewer();

  // Normalize sources: prioritize sourceDetails, then msgMetadata.source_details, then sources/citations
  const normalizedSources = useMemo(() => {
    if (sourceDetails?.length) {
      return sourceDetails.map((sd: any, idx: number) => ({
        ...sd,
        name: sd.name || sd.title || `Fuente [${idx + 1}]`,
        url: sd.url || sd.storage_url || "",
        raw_url: sd.raw_url || sd.url || "",
        pageNumber: sd.pageNumber || sd.page,
        highlight_text: sd.highlight_text || sd.referenced_content || sd.excerpt || "",
        excerpt: sd.referenced_content || sd.excerpt || sd.content || "",
        article: sd.article,
        index: sd.index !== undefined && sd.index !== null ? sd.index : (idx + 1)
      }));
    }
    if (msgMetadata?.source_details?.length) {
      return msgMetadata.source_details.map((sd: any, idx: number) => ({
        name: sd.name || sd.title || `Fuente [${idx + 1}]`,
        url: sd.url || sd.storage_url || "",
        raw_url: sd.raw_url || sd.url || "",
        pageNumber: sd.pageNumber || sd.page,
        author: sd.author,
        part_type: sd.part_type,
        is_canonical: sd.is_canonical,
        vigencia: sd.vigencia,
        retrieval_evidence: sd.retrieval_evidence,
        highlight_text: sd.highlight_text || sd.referenced_content || sd.excerpt || "",
        excerpt: sd.referenced_content || sd.excerpt || sd.content || "",
        article: sd.article,
        index: sd.index !== undefined && sd.index !== null ? sd.index : (idx + 1)
      }));
    }
    return (sources || []).map((s, i) => ({
      name: s,
      url: s.startsWith('http') ? s : '',
      raw_url: s.startsWith('http') ? s : '',
      excerpt: citations?.[i] || '',
      highlight_text: s,
      index: i + 1
    }));
  }, [sources, citations, sourceDetails, msgMetadata]);

  if (normalizedSources.length === 0) return null;

  return (
    <div className="mt-3 border-t border-slate-100 pt-2 animate-in fade-in duration-300">
      <div className="flex items-center gap-1.5 mb-1.5">
        <BookOpen className="h-3.5 w-3.5 text-blue-600" />
        <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
          Fuentes Citadas ({normalizedSources.length})
        </span>
      </div>

      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-1.5 w-full max-w-full min-w-0">
        {normalizedSources.map((source: any, idx) => {
          const displayIndex = source.index !== undefined && source.index !== null ? source.index : idx + 1;
          const cleanName = String(source.name || `Fuente_${displayIndex}`).replace(/\.pdf$/i, "").trim();
          const realUrl = source.raw_url || source.url || source.storage_url;
          const docUrl = realUrl && (realUrl.startsWith('http') || realUrl.startsWith('blob:'))
            ? realUrl
            : `citation://${encodeURIComponent(cleanName)}`;
          const pageNum = source.pageNumber || source.page || null;
          const evidence = source.retrieval_evidence;
          const isCanonical = source.is_canonical;

          return (
            <Tooltip key={idx}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  id={`source-${displayIndex}`}
                  data-source-index={displayIndex}
                  className="source-chip group rounded-lg border border-slate-200/90 bg-white text-slate-700 hover:border-blue-500 hover:bg-blue-50/70 hover:shadow-2xs transition-all flex items-center justify-between gap-1.5 px-2 sm:px-2.5 py-1 sm:py-1.5 cursor-pointer z-10 text-left w-full sm:w-auto sm:max-w-md max-w-full min-w-0 overflow-hidden box-border"
                  onClick={() => {
                    const richBody = source.referenced_content || source.excerpt || source.highlight_text || "";
                    let targetSnippet =
                      source.article && richBody && !richBody.toLowerCase().includes(String(source.article).toLowerCase())
                        ? `${source.article}. ${richBody}`
                        : (richBody.length >= (source.highlight_text || "").length ? richBody : source.highlight_text) || source.article || cleanName;
                    if (targetSnippet && targetSnippet.includes("[...]")) {
                      const parts = targetSnippet.split("[...]").map((p: string) => p.trim()).filter((p: string) => p.length > 25);
                      targetSnippet = parts[0] || targetSnippet;
                    }
                    openDocument(docUrl, source.name || cleanName, targetSnippet, pageNum, {
                      article: source.article,
                      excerpt: targetSnippet,
                      isCanonical: isCanonical,
                      vigencia: source.vigencia,
                      retrieval_evidence: evidence,
                      index: displayIndex,
                    });
                  }}
                >
                  <div className="flex items-center gap-1.5 min-w-0 flex-1 overflow-hidden">
                    <div className="w-4.5 h-4.5 rounded-md flex items-center justify-center text-[9.5px] font-black bg-blue-100 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors shrink-0">
                      {displayIndex}
                    </div>
                    <span className="text-[11.5px] sm:text-xs font-semibold truncate text-slate-800 group-hover:text-blue-700 min-w-0 flex-1 block">
                      {String(source.name || "")}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0 ml-1">
                    {pageNum && (
                      <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 shrink-0">
                        Pág. {pageNum}
                      </span>
                    )}
                    {isCanonical ? (
                      <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 shrink-0 flex items-center gap-0.5">
                        🏛️ <span className="hidden sm:inline">Canónico</span>
                      </span>
                    ) : evidence?.rrf ? (
                      <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 shrink-0">
                        ⚡ RRF
                      </span>
                    ) : null}
                    {source.part_type === 'salvamento_voto' && (
                      <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 shrink-0">
                        ⚖️ <span className="hidden sm:inline">Salvamento</span>
                      </span>
                    )}
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-slate-400 group-hover:text-blue-600 shrink-0 transition-colors"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
                  </div>
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-[calc(100vw-32px)] sm:max-w-[340px] p-3 text-xs leading-relaxed bg-slate-900 border border-slate-800 text-white rounded-xl shadow-xl z-[9999]">
                <div className="space-y-2">
                  <p className="whitespace-normal break-words line-clamp-4">
                    {source.excerpt || "Hacer clic para abrir en el visor PDF lateral."}
                  </p>
                  {(evidence || isCanonical) && (
                    <div className="pt-2 border-t border-slate-800 text-[11px] text-slate-300 space-y-1">
                      <div className="font-semibold text-blue-400 flex items-center justify-between">
                        <span>🔍 Trazabilidad (Provenance):</span>
                        {source.vigencia && <span className="text-emerald-400 font-bold">{source.vigencia}</span>}
                      </div>
                      {isCanonical ? (
                        <p className="text-emerald-300">Norma canónica oficial vinculada a Supabase.</p>
                      ) : (
                        <>
                          {evidence?.rrf?.hybrid_score && (
                            <p className="text-slate-400">Score Híbrido RRF: <span className="font-mono text-white">{evidence.rrf.hybrid_score}</span></p>
                          )}
                          {evidence?.dense?.hits?.length > 0 && (
                            <p className="truncate text-slate-400">
                              Vectorial: Top {evidence.dense.hits[0].rank} ({evidence.dense.hits.length} consultas)
                            </p>
                          )}
                          {evidence?.bm25?.hits?.length > 0 && (
                            <p className="truncate text-slate-400">
                              Léxico BM25: Top {evidence.bm25.hits[0].rank} ({evidence.bm25.hits.length} consultas)
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};


interface ClarificationFormField {
  id: string;
  label: string;
  defaultValue?: string;
  options: string[];
}

interface ClarificationFormData {
  title: string;
  badge?: string;
  fields: ClarificationFormField[];
  submitLabel: string;
}

const ClarificationFormCard = ({
  form,
  onSubmit,
  disabled = false,
}: {
  form: ClarificationFormData;
  onSubmit: (query: string) => void;
  disabled?: boolean;
}) => {
  const [selections, setSelections] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    (form.fields || []).forEach((field) => {
      initial[field.id] = field.defaultValue || field.options?.[0] || "";
    });
    return initial;
  });
  const [submitted, setSubmitted] = useState(false);

  const handleOptionChange = (fieldId: string, option: string) => {
    if (submitted || disabled) return;
    setSelections((prev) => ({ ...prev, [fieldId]: option }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (submitted || disabled) return;
    setSubmitted(true);
    const parts = (form.fields || []).map((f) => {
      const label = f.label.replace(/[¿?]/g, "").trim();
      return `${label}: ${selections[f.id] || f.options?.[0] || ""}`;
    });
    onSubmit(`[Formulario] ${parts.join(" | ")}`);
  };

  return (
    <div className="mt-3.5 mb-2.5 rounded-2xl bg-slate-900 border border-slate-800 p-4 sm:p-5 text-white shadow-lg animate-in fade-in slide-in-from-bottom-2 duration-300">
      {form.badge && (
        <div className="text-[11px] font-semibold text-slate-400 tracking-wide mb-1.5">
          {form.badge}
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        {(form.fields || []).map((field, fIdx) => (
          <div key={field.id} className={fIdx > 0 ? "pt-3 border-t border-slate-800/80" : ""}>
            <h4 className="text-xs sm:text-sm font-semibold text-slate-100 mb-2.5 leading-snug">
              {field.label}
            </h4>
            <div className="space-y-2">
              {(field.options || []).map((opt) => {
                const isSelected = selections[field.id] === opt;
                return (
                  <label
                    key={opt}
                    className={`flex items-center gap-3 cursor-pointer py-1 px-1.5 rounded-lg transition-colors ${
                      disabled || submitted ? "opacity-75 cursor-not-allowed" : "hover:bg-slate-800/60"
                    }`}
                  >
                    <input
                      type="radio"
                      name={`field_${field.id}`}
                      value={opt}
                      checked={isSelected}
                      disabled={disabled || submitted}
                      onChange={() => handleOptionChange(field.id, opt)}
                      className="w-4 h-4 text-blue-600 bg-slate-800 border-slate-600 focus:ring-blue-500 focus:ring-offset-slate-900"
                    />
                    <span className={`text-xs sm:text-[13px] ${isSelected ? "text-white font-medium" : "text-slate-300"}`}>
                      {opt}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
        <div className="pt-2">
          <button
            type="submit"
            disabled={disabled || submitted}
            className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-white text-slate-950 font-semibold text-xs sm:text-sm hover:bg-slate-200 active:scale-[0.99] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            {submitted ? "Opción enviada..." : (form.submitLabel || "Profundizar")}
          </button>
        </div>
      </form>
    </div>
  );
};

const RelatedQuestions = ({
  questions,
  onSelect,
}: {
  questions: string[];
  onSelect: (query: string) => void;
}) => (
  <div className="mt-3 sm:mt-4 mb-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
    <div className="flex items-center gap-1.5 mb-1.5 px-1">
      <div className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white shadow-2xs">
        <Sparkles className="h-3 w-3" />
      </div>
      <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
        Preguntas de seguimiento
      </span>
    </div>
    <div className="flex flex-col gap-0.5 w-full max-w-full min-w-0">
      {questions.map((question, idx) => (
        <button
          key={idx}
          type="button"
          onClick={() => onSelect(question)}
          className="group flex w-full max-w-full min-w-0 items-start gap-2 rounded-lg px-2 py-1.5 text-left text-xs sm:text-[13px] text-slate-700 transition-colors hover:bg-slate-50 overflow-hidden"
        >
          <CornerDownRight
            className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400 group-hover:text-blue-500"
            aria-hidden
          />
          <span className="leading-snug group-hover:text-slate-900 min-w-0 flex-1 break-words [overflow-wrap:anywhere]">{question}</span>
        </button>
      ))}
    </div>
  </div>
);

const DocumentActionsPanel = ({
  onAction,
  onToggleContext,
  isFocusedContext,
  isRagReady,
  isVectorizing,
  isUploading,
  requiresIndexing
}: {
  onAction: (prompt: string) => void;
  onToggleContext: (val: boolean) => void;
  isFocusedContext: boolean;
  isRagReady: boolean;
  isVectorizing: boolean;
  isUploading: boolean;
  requiresIndexing: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const isLocked = isUploading || isVectorizing || (requiresIndexing && !isRagReady);

  const actions = [
    { label: "Borrador", icon: <PenLine className="h-3.5 w-3.5" />, prompt: "genera un borrador jurídico." },
    { label: "Resumir", icon: <FileSearch className="h-3.5 w-3.5" />, prompt: "resume los puntos clave." },
    { label: "Ideas", icon: <Lightbulb className="h-3.5 w-3.5" />, prompt: "extrae las ideas principales." },
    { label: "Análisis preliminar", icon: <Glasses className="h-3.5 w-3.5" />, prompt: "realiza un análisis jurídico preliminar." },
    { label: "Informe", icon: <ClipboardList className="h-3.5 w-3.5" />, prompt: "genera un informe detallado." },
    { label: "Argumentos", icon: <Gavel className="h-3.5 w-3.5" />, prompt: "identifica y desarrolla los argumentos legales." },
    // { label: "Relacionar ideas", icon: <Combine className="h-3.5 w-3.5" />, prompt: "relaciona las ideas con el contexto legal." },
    { label: "Generar comunicación", icon: <MessageSquare className="h-3.5 w-3.5" />, prompt: "redacta una comunicación formal." },
    //{ label: "Esquema", icon: <LayoutList className="h-3.5 w-3.5" />, prompt: "crea un esquema estructurado." },
    //{ label: "Generar diagrama", icon: <Share2 className="h-3.5 w-3.5" />, prompt: "esquematiza la lógica en pasos o procesos." },
  ];

  return (
    <div className="mb-4 bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div
        className="px-4 py-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between cursor-pointer hover:bg-slate-100 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <FileText className="h-3.5 w-3.5 text-blue-600" />
          <span className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Acciones sobre el documento de edición:</span>
          <span
            className={`ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide ${isRagReady
              ? "bg-emerald-100 text-emerald-700"
              : (isUploading || isVectorizing)
                ? "bg-amber-100 text-amber-700"
                : "bg-slate-100 text-slate-500"
              }`}
          >
            {isRagReady ? (
              <>
                <CheckCircle2 className="h-3 w-3" />
                Documento listo
              </>
            ) : (isUploading || isVectorizing) ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                Preparando...
              </>
            ) : (
              <>
                <AlertCircle className="h-3 w-3" />
                Sin indexar
              </>
            )}
          </span>
        </div>
        {isExpanded ? <ChevronUp className="h-3.5 w-3.5 text-slate-400" /> : <ChevronDown className="h-3.5 w-3.5 text-slate-400" />}
      </div>

      {isExpanded && (
        <div className="p-3">
          <div className="flex flex-wrap gap-2 mb-3">
            {actions.map((action, idx) => (
              <button
                key={idx}
                disabled={isLocked}
                onClick={() => onAction(action.prompt)}
                className="flex items-center gap-2 px-2.5 py-1.5 rounded-full border border-slate-200 bg-white hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 transition-all text-[10px] font-bold text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {action.icon}
                {action.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3 pt-2.5 border-t border-slate-50">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={isFocusedContext}
                disabled={isLocked}
                onChange={(e) => onToggleContext(e.target.checked)}
              />
              <div className="w-8 h-4.5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3.5 after:w-3.5 after:transition-all peer-checked:bg-blue-600"></div>
              <span className="ms-2.5 text-[10px] font-bold text-slate-500 uppercase tracking-tight flex items-center gap-1.5">
                <FileSearch className="h-3 w-3" />
                Consultar sobre el documento
              </span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

const LoadingSpinner = () => (
  <div className="flex items-center gap-3 p-4 rounded-lg bg-gray-50 max-w-[100%]">
    <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
    <span className="text-gray-600">Analizando consulta legal...</span>
  </div>
);

// Local TraceStep interface removed as it conflicts with import from contexts/AgentExecutionContext

const TraceProgress = ({ 
  steps, 
  defaultExpanded = false,
  executionTime
}: { 
  steps: TraceStep[]; 
  defaultExpanded?: boolean; 
  executionTime?: number | string;
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  useEffect(() => {
    if (defaultExpanded) {
      setIsExpanded(true);
    }
  }, [defaultExpanded]);

  if (!steps || steps.length === 0) return null;

  const visibleSteps = steps.filter((s) => s.message && String(s.message).trim().length > 0);
  if (visibleSteps.length === 0) return null;

  const runningStep = visibleSteps.find((s) => s.status === "running");
  const completedCount = visibleSteps.filter((s) => s.status === "done" || (s.status as string) === "completed" || (s.status as string) === "success").length;
  const totalSteps = visibleSteps.length;
  const currentStepIndex = runningStep ? visibleSteps.indexOf(runningStep) + 1 : totalSteps;
  const progressPercent = Math.round((completedCount / totalSteps) * 100);

  // Helper to remove emojis from pipeline messages and badges
  const stripEmojis = (str: string) => 
    str.replace(/\p{Extended_Pictographic}/gu, '').replace(/[🏛️📜📄🔍🔎📑⚖️✍️🧠🔹]/g, '').trim();

  const activeMessage = runningStep
    ? stripEmojis(typeof runningStep.message === "object" ? JSON.stringify(runningStep.message) : String(runningStep.message || ""))
    : "Auditoría Forense MiA";

  // Clean Badge Formatter
  const renderCleanBadge = (badge: string, idx: number) => {
    const isCourt = badge.includes("🏛️") || /sentencia|corte\s+const|corte\s+suprema|consejo\s+de\s+estado/i.test(badge);
    const isStatute = badge.includes("📜") || /ley|c[oó]digo|decreto|constituci[oó]n|c\.?p\.?/i.test(badge);
    const isConcept = badge.includes("📄") || /concepto/i.test(badge);
    const cleanText = stripEmojis(badge);

    const styleClass = isCourt
      ? "bg-indigo-50/90 text-indigo-700 border-indigo-200/80 hover:bg-indigo-100"
      : isStatute
        ? "bg-emerald-50/90 text-emerald-800 border-emerald-200/80 hover:bg-emerald-100"
        : isConcept
          ? "bg-amber-50/90 text-amber-800 border-amber-200/80 hover:bg-amber-100"
          : "bg-slate-100/90 text-slate-700 border-slate-200/80 hover:bg-slate-200";

    return (
      <span
        key={idx}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium border transition-colors shadow-2xs ${styleClass}`}
      >
        {cleanText}
      </span>
    );
  };

  // Group steps by legal epistemic phases
  const getStepPhase = (s: TraceStep): string => {
    if (s.phase) {
      if (s.phase.includes("qualif")) return "qualification";
      if (s.phase.includes("ontol")) return "ontology";
      if (s.phase.includes("evid")) return "evidence";
      if (s.phase.includes("synth")) return "synthesis";
    }
    const msgLow = (s.message || "").toLowerCase();
    if (msgLow.includes("query agent:") || msgLow.includes("router agent:") || msgLow.includes("clasificación recomendada")) return "qualification";
    if (msgLow.includes("sub-query") || msgLow.includes("query & aggregation agent:")) return "ontology";
    if (msgLow.includes("rerank agent:")) return "evidence";
    if (msgLow.includes("answer agent:") || msgLow.includes("generando respuesta")) return "synthesis";
    const text = ((s.query || "") + " " + msgLow + " " + (s.detail || "")).toLowerCase();
    if (text.includes("analizando") || text.includes("marco jurídico") || text.includes("zero prior") || text.includes("materia:")) return "qualification";
    if (text.includes("pilar") || (s.badges && s.badges.length > 0) || text.includes("pentágono") || s.objective) return "ontology";
    if (text.includes("filtro") || text.includes("vigencia") || text.includes("derogaci") || text.includes("admisibilidad") || text.includes("eliminadas fuentes") || text.includes("cobertura") || text.includes("divergencia")) return "evidence";
    return "synthesis";
  };

  const phaseConfig = [
    { id: "qualification", name: "Fase 1: Calificación Dogmática e IssueLock" },
    { id: "ontology", name: "Fase 2: Pentágono Ontológico y Búsqueda" },
    { id: "evidence", name: "Fase 3: Juez de Evidencia y Control de Vigencia" },
    { id: "synthesis", name: "Fase 4: Ponderación Hermenéutica y Dictamen" },
  ];

  const groupedPhases = phaseConfig.map((p) => ({
    ...p,
    steps: visibleSteps.filter((s) => getStepPhase(s) === p.id),
  })).filter((p) => p.steps.length > 0);

  const totalSourcesCount = visibleSteps.reduce((acc, s) => acc + (s.badges?.length || 0) + (s.more_count || 0), 0);

  return (
    <div className="my-1 sm:my-1.5 rounded-xl border border-slate-200/80 bg-white shadow-2xs overflow-hidden transition-all duration-300">
      {/* Compact Single-line Status Header Bar */}
      <div 
        onClick={() => setIsExpanded(!isExpanded)}
        className={`px-2.5 sm:px-3 py-1.5 sm:py-2 bg-gradient-to-r from-slate-50 to-white flex items-center justify-between cursor-pointer select-none hover:bg-slate-50/90 transition-colors ${
          isExpanded ? "border-b border-slate-100" : ""
        }`}
      >
        <div className="flex items-center gap-2 sm:gap-2.5 min-w-0 flex-1 mr-2">
          <div className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 border transition-all ${
            runningStep 
              ? "bg-blue-50 border-blue-200 text-blue-600 shadow-2xs animate-pulse" 
              : "bg-emerald-50 border-emerald-200 text-emerald-600"
          }`}>
            {runningStep ? (
              <Loader2 className="h-3 w-3 animate-spin text-blue-600" />
            ) : (
              <CheckCircle2 className="h-3 w-3 text-emerald-600" />
            )}
          </div>
          
          <div className="flex items-center gap-2 min-w-0 flex-1 flex-wrap">
            <span className="text-xs font-bold text-slate-800 shrink-0">
              Auditoría Forense MiA
            </span>
            {runningStep && (
              <>
                <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 shrink-0">
                  Paso {currentStepIndex} de {totalSteps}
                </span>
                <span className="text-xs text-slate-600 font-medium truncate max-w-[260px] sm:max-w-[420px]">
                  {activeMessage}
                </span>
              </>
            )}
            {!runningStep && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 shrink-0 flex items-center gap-1">
                ✓ {groupedPhases.length} fases validadas
                {totalSourcesCount > 0 ? ` • ${totalSourcesCount} fuentes cotejadas` : ""}
                {executionTime ? ` • ${Number(executionTime).toFixed(1)}s` : ""}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {runningStep && (
            <div className="hidden sm:flex items-center gap-1.5 w-20">
              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-600 rounded-full transition-all duration-500" 
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <span className="text-[10px] font-bold text-slate-400">{progressPercent}%</span>
            </div>
          )}
          <span className="text-[11px] font-semibold text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-1">
            {isExpanded ? "Ocultar pasos" : (runningStep ? "Ver detalles" : "Ver auditoría")}
            {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </span>
        </div>
      </div>

      {/* Expanded Stepper Details */}
      {isExpanded && (
        <div className="p-3.5 bg-slate-50/50 border-t border-slate-100 space-y-3 animate-in fade-in slide-in-from-top-1 duration-200">
          {groupedPhases.map((phaseGroup, pIdx) => {
            const phaseRunning = phaseGroup.steps.some(s => s.status === "running");

            return (
              <div key={pIdx} className="bg-white rounded-xl border border-slate-200/80 p-3 shadow-2xs">
                {/* Phase Header */}
                <div className="flex items-center justify-between pb-2 mb-2 border-b border-slate-100">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs font-bold text-slate-800 truncate">
                      {phaseGroup.name}
                    </span>
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-wider shrink-0 ${
                    phaseRunning ? "text-blue-600 animate-pulse" : "text-emerald-600"
                  }`}>
                    {phaseRunning ? "En proceso" : "Listo"}
                  </span>
                </div>

                {/* Phase Steps */}
                <div className="space-y-2 pl-1">
                  {phaseGroup.steps.map((step, sIdx) => {
                    const stepRunning = step.status === "running";
                    const isError = step.status === "error";
                    const stepText = typeof step.message === "object" ? JSON.stringify(step.message) : String(step.message || "");

                    return (
                      <div key={sIdx} className="text-xs">
                        <div className="flex items-center gap-1.5 font-semibold text-slate-800">
                          <span className={stepRunning ? "text-blue-900 font-bold" : isError ? "text-rose-800" : "text-slate-900"}>
                            {stripEmojis(step.query || stepText)}
                          </span>
                          {step.elapsed_ms && (
                            <span className="text-[10px] font-normal text-slate-400">
                              ({step.elapsed_ms}ms)
                            </span>
                          )}
                        </div>

                        {step.objective && (
                          <p className="mt-0.5 text-[11px] text-slate-500 italic pl-5 leading-relaxed">
                            {step.objective}
                          </p>
                        )}

                        {step.badges && step.badges.length > 0 && (
                          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 pl-5">
                            {step.badges.map((b, bIdx) => renderCleanBadge(b, bIdx))}
                            {(step.more_count || 0) > 0 && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200/80">
                                +{step.more_count} más
                              </span>
                            )}
                          </div>
                        )}

                        {!step.badges?.length && step.detail && (
                          <div className="mt-1 pt-1 text-[11px] text-slate-600 font-mono whitespace-pre-line pl-5 leading-relaxed">
                            {step.detail}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const MessageContent = ({
  content,
  highlights = [],
  sources = []
}: {
  content: string;
  highlights?: Highlight[];
  sources?: any[];
}) => {
  const { openDocument } = useDocumentViewer();

  const displayContent = useMemo(() => {
    let dc = content.split(/---QUESTIONS---|---ACTIONS---/i)[0].trim();
    // Strip enclosing markdown code block wrappers (full and streaming)
    dc = dc.replace(/^```(?:markdown|md)?\s*\r?\n/i, "");
    dc = dc.replace(/\r?\n```\s*$/i, "");
    // Strip all "Fuentes" markdown sections cleanly IF rendered visually below in SourcesList
    if (sources && sources.length > 0) {
      let prevDc = "";
      const fuentesRegex = /(?:\r?\n\s*(?:---|___|\*\*\*)\s*)?(?:\r?\n|^)\s*(?:#{1,6}\s*|[*_]{1,2})?(?:\d+[.)]\s*)?(?:[*_]{1,2})?\s*\bFuentes(?:\s+(?:consultadas|citadas))?\b\s*(?:[*_]{1,2})?\s*:?[\s\S]*?(?=(?:\r?\n\s*(?:#{1,6}\s+|---|___|\*\*\*|\*\*⚠️|⚠️|\bAviso\s+legal\b))|$)/gi;
      while (prevDc !== dc) {
        prevDc = dc;
        dc = dc.replace(fuentesRegex, "").trim();
      }
      dc = dc.replace(/(?:\r?\n\s*(?:---|___|\*\*\*)\s*){2,}/g, "\n\n---\n\n").trim();
    }
    // Normalize pre-linked [[1]](#source-1) or [1](#source-1) back to [1]
    dc = dc.replace(/\[+\[?(\d+)\]?\s*\(\s*#source-\d+\s*\)\]*/g, "[$1]");
    dc = dc.replace(/\[\[?(\d+)\]?\]\(#source-\d+\)/g, "[$1]");
    dc = dc.replace(/\(([A-Z\d\s\-.]+\.pdf)(?:,\s*pág\.\s*\d+)?\)/gi, (match, fileName: string) => {
      return `[${match}](#source-find-${encodeURIComponent(fileName.trim())})`;
    });
    return dc;
  }, [content, sources]);

  const html = useMemo(() => {
    try {
      const raw = marked.parse(displayContent) as string;
      const base =
        raw && String(raw).trim().length > 0
          ? String(raw)
          : `<p class="whitespace-pre-wrap">${escapeHtmlPlain(displayContent)}</p>`;

      // Convert ANY citation bracket [1], [2], [3] or [Ref: ...] in the HTML text into an interactive, clickable chip
      const withRefChips = base.replace(
        /(?<!<[^>]*)\[Ref:\s*([^\]]+)\](?![^<]*>)/gi,
        (_match, refBody) => {
          const safeRef = String(refBody || "").trim();
          return `<button type="button" class="inline-citation-chip inline-flex items-center justify-center font-semibold text-xs px-1.5 py-0.5 mx-0.5 rounded-md bg-slate-100 text-slate-700 hover:bg-blue-600 hover:text-white border border-slate-300 hover:border-blue-600 transition-all cursor-pointer select-none shadow-2xs group/chip align-baseline" data-ref-citation="${encodeURIComponent(safeRef)}" title="Hacer clic para abrir el documento citado">[Ref: ${safeRef}]</button>`;
        }
      );
      const withCitations = withRefChips.replace(
        /(?<!<[^>]*)\[(\d+)\](?![^<]*>)/g,
        (_match, p1) => {
          return `<button type="button" class="inline-citation-chip inline-flex items-center justify-center font-bold text-xs px-1.5 py-0.5 mx-0.5 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-600 hover:text-white border border-blue-200 hover:border-blue-600 transition-all cursor-pointer select-none shadow-2xs group/chip align-baseline whitespace-nowrap" data-citation="${p1}" title="Hacer clic para ver documento y resaltar cita">[${p1}]</button><wbr/>`;
        }
      );

      return applyHighlightsToRenderedHtml(withCitations, highlights || []);
    } catch {
      return `<p class="whitespace-pre-wrap">${escapeHtmlPlain(displayContent)}</p>`;
    }
  }, [displayContent, highlights]);

  const onMarkdownClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // 1. Check if user clicked an inline citation chip
      const chip = (e.target as HTMLElement).closest("[data-citation], [data-ref-citation], .inline-citation-chip, a[href*='#source-']");
      if (chip) {
        e.preventDefault();
        e.stopPropagation();

        const refCitationAttr = chip.getAttribute("data-ref-citation");
        if (refCitationAttr && sources && sources.length > 0) {
          const decodedRef = decodeURIComponent(refCitationAttr).toLowerCase();
          const parts = decodedRef.split(",").map((p) => p.trim());
          const docPart = parts[0] || "";
          const artPart = parts[1] || "";
          const matchedSource = sources.find((s: any) => {
            const sName = String(s.name || "").toLowerCase();
            const sArt = String(s.article || "").toLowerCase();
            const docMatch = docPart && (sName.includes(docPart) || docPart.includes(sName.split("(")[0].trim()));
            if (!docMatch) return false;
            if (artPart && sArt) {
              const artNum = artPart.replace(/[^\d]/g, "");
              return artNum ? sArt.includes(artNum) || sName.includes(artNum) : true;
            }
            return true;
          }) || sources.find((s: any) => {
            const sName = String(s.name || "").toLowerCase();
            return docPart && (sName.includes(docPart) || docPart.includes(sName.split("(")[0].trim()));
          }) || sources[0];
          if (matchedSource) {
            const cleanName = String(matchedSource.name || "Documento").replace(/\.pdf$/i, "").trim();
            const realUrl = matchedSource.raw_url || matchedSource.url || matchedSource.storage_url;
            const docUrl = realUrl && (realUrl.startsWith('http') || realUrl.startsWith('blob:'))
              ? realUrl
              : `citation://${encodeURIComponent(cleanName)}`;
            const explicitArtNum = artPart ? artPart.replace(/[^\d\w\-]/g, "").replace(/^(?:art(?:iculo|ículo)?)/i, "").trim() : "";
            const targetSnippet = explicitArtNum
              ? `Artículo ${explicitArtNum}`
              : (matchedSource.highlight_text || matchedSource.article || matchedSource.excerpt || cleanName);
            const pageNum = matchedSource.pageNumber || matchedSource.page || null;
            openDocument(docUrl, matchedSource.name || cleanName, targetSnippet, pageNum, {
              article: matchedSource.article || (explicitArtNum ? `Artículo ${explicitArtNum}` : undefined),
              excerpt: targetSnippet,
              isCanonical: matchedSource.is_canonical,
            });
            return;
          }
        }

        const citationStr = chip.getAttribute("data-citation") || 
          (chip.getAttribute("href") || "").replace("#source-", "") ||
          chip.textContent?.replace(/[\[\]]/g, "").trim() || "";

        const idx = parseInt(citationStr, 10);
        if (idx) {
          // Check sources passed in: match explicit index first, then array index
          let source = sources
            ? (sources.find((s: any) => s.index === idx) || sources[idx - 1])
            : null;

          // If not in sources or default placeholder, check parsed from content
          if (!source || !source.name || source.name.startsWith("Fuente ")) {
            const parsed = parseSourcesFromContent(content);
            if (parsed?.items) {
              const matchedItem = parsed.items.find((it) => it.index === idx);
              if (matchedItem) {
                source = {
                  name: matchedItem.name,
                  url: matchedItem.name.startsWith("http") ? matchedItem.name : "",
                  raw_url: matchedItem.name.startsWith("http") ? matchedItem.name : "",
                  pageNumber: matchedItem.pageNumber,
                  excerpt: matchedItem.citation,
                  highlight_text: matchedItem.name,
                  index: matchedItem.index
                };
              }
            }
            if (!source && parsed && parsed.sources[idx - 1]) {
              const rawCitation = parsed.citations[idx - 1] || "";
              const pageMatch = (parsed.sources[idx - 1] + " " + rawCitation).match(/p(?:[áa]g(?:s|\.|\.s)?)\s*[:.]?\s*(\d+)/i);
              source = {
                name: parsed.sources[idx - 1],
                url: parsed.sources[idx - 1].startsWith("http") ? parsed.sources[idx - 1] : "",
                raw_url: parsed.sources[idx - 1].startsWith("http") ? parsed.sources[idx - 1] : "",
                pageNumber: pageMatch ? parseInt(pageMatch[1], 10) : undefined,
                excerpt: rawCitation || parsed.sources[idx - 1],
                highlight_text: parsed.sources[idx - 1],
                index: idx
              };
            }
          }

          // Fallback: search regex in content for "[idx] ..."
          if (!source) {
            const pattern = new RegExp(`\\[${idx}\\]\\s*([^\\n]+)`, "i");
            const match = content.match(pattern);
            if (match) {
              const matchedLine = match[1].trim();
              const pageMatch = matchedLine.match(/p(?:[áa]g(?:s|\.|\.s)?)\s*[:.]?\s*(\d+)/i);
              source = {
                name: matchedLine,
                url: "",
                raw_url: "",
                pageNumber: pageMatch ? parseInt(pageMatch[1], 10) : undefined,
                excerpt: matchedLine,
                highlight_text: matchedLine,
                index: idx
              };
            }
          }

          let rawName = source?.name ? String(source.name) : `Fuente_${idx}`;
          // Strip any stray markdown brackets/links like "]]#source-1"
          rawName = rawName.replace(/\[\[?|\(?#source[^\)]*\)?|\]\]?/g, "").replace(/\.pdf$/i, "").trim();
          rawName = rawName.replace(/^[^\w\sáéíóúÁÉÍÓÚñÑ]+|[^\w\sáéíóúÁÉÍÓÚñÑ]+$/g, "").trim();
          const cleanName = rawName || `Fuente_${idx}`;
          const pageNum = source?.pageNumber || (source as any)?.page || null;
          const realUrl = source?.raw_url || source?.url || source?.storage_url;
          const docUrl = realUrl && (realUrl.startsWith('http') || realUrl.startsWith('blob:'))
            ? realUrl
            : `citation://${encodeURIComponent(cleanName)}`;
          const richBody = (source as any)?.referenced_content || source?.excerpt || source?.highlight_text || "";
          let targetSnippet =
            source?.article && richBody && !richBody.toLowerCase().includes(String(source.article).toLowerCase())
              ? `${source.article}. ${richBody}`
              : (richBody.length >= (source?.highlight_text || "").length ? richBody : source?.highlight_text) || source?.article || cleanName;
          if (targetSnippet && targetSnippet.includes("[...]")) {
            const parts = targetSnippet.split("[...]").map((p: string) => p.trim()).filter((p: string) => p.length > 25);
            targetSnippet = parts[0] || targetSnippet;
          }
          openDocument(docUrl, source?.name || cleanName, targetSnippet, pageNum, {
            article: source?.article,
            excerpt: targetSnippet,
            index: idx,
          });
          return;
        }
      }

      // 2. Regular anchor click
      const anchor = (e.target as HTMLElement).closest("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;

      e.preventDefault();
      if (!href.startsWith("#")) {
        openDocument(href, undefined, undefined);
      }
    },
    [openDocument, sources, content]
  );

  return (
    <div className="prose prose-sm max-w-none dark:prose-invert prose-headings:font-semibold prose-p:text-gray-600 prose-p:leading-7 w-full max-w-full min-w-0 overflow-hidden">
      <div
        className="chat-md-html select-text text-gray-600 text-[14.5px] sm:text-[15px] leading-relaxed sm:leading-7 break-words [overflow-wrap:anywhere] w-full max-w-full min-w-0 [&_*]:break-words [&_*]:[overflow-wrap:anywhere] [&_*]:max-w-full [&_a]:text-blue-600 [&_a:hover]:text-blue-800 [&_a]:underline [&_a]:underline-offset-2 [&_a]:cursor-pointer [&_a]:break-all [&_h1]:text-xl sm:[&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mt-6 [&_h1]:mb-4 [&_h1]:text-gray-900 [&_h1]:border-b [&_h1]:pb-2 [&_h2]:text-lg sm:[&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-5 [&_h2]:mb-3 [&_h2]:text-gray-800 [&_h3]:text-base sm:[&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-2 [&_h3]:text-gray-800 [&_p]:mb-4 [&_p]:break-words [&_ul]:list-disc [&_ul]:pl-5 sm:[&_ul]:pl-6 [&_ul]:mb-4 [&_ul]:space-y-2 [&_ol]:list-decimal [&_ol]:pl-5 sm:[&_ol]:pl-6 [&_ol]:mb-4 [&_ol]:space-y-2 [&_li]:mb-1 [&_li]:break-words [&_strong]:font-semibold [&_strong]:text-gray-900 [&_em]:italic [&_em]:text-gray-800 [&_blockquote]:border-l-4 [&_blockquote]:border-blue-200 [&_blockquote]:bg-blue-50 [&_blockquote]:pl-4 [&_blockquote]:py-2 [&_blockquote]:my-4 [&_blockquote]:text-gray-700 [&_blockquote]:italic [&_blockquote]:rounded-r [&_code]:rounded [&_code]:break-all [&_pre]:bg-gray-50 [&_pre]:p-3 sm:[&_pre]:p-4 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_pre]:max-w-full [&_pre]:whitespace-pre-wrap [&_pre]:break-words [&_pre]:my-4 [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_table]:w-full [&_table]:max-w-full [&_table]:border-collapse [&_table]:text-sm [&_table]:bg-white [&_th]:px-3 sm:[&_th]:px-4 [&_th]:py-2.5 sm:[&_th]:py-3 [&_th]:text-left [&_th]:font-semibold [&_th]:border [&_td]:px-3 sm:[&_td]:px-4 [&_td]:py-2.5 sm:[&_td]:py-3 [&_td]:border [&_.table-wrapper]:overflow-x-auto [&_.table-wrapper]:max-w-full [&_.table-wrapper]:my-6 [&_.table-wrapper]:rounded-lg [&_.table-wrapper]:border [&_.table-wrapper]:border-gray-200"
        onClick={onMarkdownClick}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
};

const DocumentDraftCard = ({
  title,
  content,
  documentUrl,
  documentId,
  onDownloadPDF,
  onDownloadWord,
}: {
  title: string;
  content: string;
  documentUrl: string;
  documentId: string;
  onDownloadPDF: (content: string, title: string) => void;
  onDownloadWord: (url: string) => void;
}) => {
  const navigate = useNavigate();

  return (
    <div className="mt-8 mb-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <Card className="border-slate-300 shadow-xl overflow-hidden bg-[#fafafa] group transition-all duration-500 max-w-2xl">
        {/* Professional Header */}
        <div className="bg-slate-900 px-6 py-4 flex items-center justify-between border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-800 rounded text-slate-300 border border-slate-700">
              <FileText className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-sm font-bold text-slate-100 tracking-tight uppercase">{title}</CardTitle>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Borrador Técnico v1.0</span>
                <span className="w-1 h-1 rounded-full bg-slate-600" />
                <span className="text-[9px] font-bold text-blue-400 uppercase tracking-widest">Certificado SarAI</span>
              </div>
            </div>
          </div>
          <div className="hidden sm:flex flex-col items-end">
            <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-800/50 border border-slate-700 rounded text-[9px] font-bold text-slate-300 uppercase tracking-tighter">
              <Gavel className="h-3 w-3 text-slate-400" />
              Jurisprudencia CO
            </div>
          </div>
        </div>

        <CardContent className="p-0">
          {/* Document Preview Area */}
          <div className="relative group/preview bg-white">
            <div className="absolute top-4 right-4 z-20 opacity-40 group-hover/preview:opacity-100 transition-opacity">
              <div className="w-16 h-16 border-4 border-slate-100 rounded-full flex items-center justify-center -rotate-12">
                <div className="text-[8px] font-black text-slate-200 text-center uppercase leading-none">
                  Documento<br />Oficial
                </div>
              </div>
            </div>

            <div className="p-8 font-serif text-slate-800 text-[13px] leading-relaxed max-h-[320px] overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white/95 z-10" />
              <div className="whitespace-pre-wrap select-none border-l-2 border-slate-100 pl-6 italic text-slate-700">
                {content}
              </div>
            </div>

            <div className="absolute bottom-6 left-0 right-0 flex justify-center z-20">
              <Button
                onClick={() => navigate(`/dashboard/collab/${documentId}`, {
                  state: { content, documentType: title, isNew: true }
                })}
                variant="secondary"
                className="h-9 px-6 bg-slate-900 text-white hover:bg-slate-800 shadow-xl border border-slate-700 rounded-md text-[11px] font-bold uppercase tracking-wider transition-all hover:scale-105 active:scale-95"
              >
                Revisar y Editar Documento
                <ArrowRight className="h-3.5 w-3.5 ml-2" />
              </Button>
            </div>
          </div>

          {/* Action Footer */}
          <div className="grid grid-cols-2 border-t border-slate-100 divide-x divide-slate-100 bg-slate-50/50">
            <button
              className="flex items-center justify-center gap-3 py-4 text-[11px] font-bold text-slate-600 hover:text-slate-900 hover:bg-white transition-all uppercase tracking-widest"
              onClick={() => onDownloadPDF(content, title)}
            >
              <FileDown className="h-4 w-4" />
              Exportar PDF
            </button>
            <button
              className="flex items-center justify-center gap-3 py-4 text-[11px] font-bold text-slate-600 hover:text-slate-900 hover:bg-white transition-all uppercase tracking-widest"
              onClick={() => onDownloadWord(documentUrl)}
            >
              <Download className="h-4 w-4" />
              Word (DOCX)
            </button>
          </div>
        </CardContent>
      </Card>
      <p className="mt-3 text-[10px] text-slate-400 font-medium italic text-center max-w-2xl">
        * Este es un borrador generado por IA basado en técnica jurídica colombiana. Debe ser revisado por un profesional.
      </p>
    </div>
  );
};


// Extraction of focus content (removes greetings/closings for cleaner documents)
const extractDocumentContent = (content: string, metadata?: any): { body: string; isDocument: boolean } => {
  // 1. Explicit AI Intent check (the most reliable source)
  const isDraftingIntent = metadata?.intent === "drafting" || metadata?.intent === "redacción";

  // 2. Look for code blocks (often used for standalone documents)
  const codeBlockMatch = content.match(/```(?:markdown|text|doc)?\n([\s\S]*?)```/i);
  if (codeBlockMatch && codeBlockMatch[1].length > 250) {
    return { body: codeBlockMatch[1].trim(), isDocument: true };
  }

  // 3. Identify the core "document" by stripping common conversational headers
  const formalStartMatch = content.match(/(?:^|\n)(?:# |## |### |CONTRATO|MEMORANDO|DEMANDA|PETICIÓN|TRATADO|PAGO|RECIBO|ACCIÓN DE TUTELA)[\s\S]+/i);

  if (formalStartMatch) {
    const body = formalStartMatch[0].trim();
    // Heuristic: only treat it as a standalone document if it's substantial (>800 chars)
    // or if the backend explicitly told us it's a drafting task
    const isSubstantial = body.length > 800;
    if (isSubstantial || isDraftingIntent) {
      return { body, isDocument: true };
    }
  }

  // 4. Default: remove common AI filler patterns and return as non-document body
  const cleaned = content
    .replace(/^(?:Claro|Por supuesto|Aquí tienes|Entendido|Hola|He generado|Con gusto)[\s\S]*?(?=\n\n|# |## |\*\*)/i, '')
    .trim();

  return { body: cleaned, isDocument: isDraftingIntent };
};

function extractClarificationFormFromContent(content: string): { body: string; form: ClarificationFormData | null } {
  if (!content?.trim()) return { body: content, form: null };

  // Detectar bloque JSON con "title" y "fields"
  const formMatch = content.match(/(?:```(?:clarification_form|json)?\s*)?(\{\s*"title"\s*:\s*"[^"]*"[\s\S]*?"fields"\s*:\s*\[[\s\S]*?\}\s*)(?:```)?/);
  if (formMatch && formMatch.index !== undefined) {
    const rawJson = formMatch[1].trim();
    try {
      const parsed = JSON.parse(rawJson);
      if (parsed && typeof parsed === "object" && parsed.title && Array.isArray(parsed.fields)) {
        const cleanBody = content.slice(0, formMatch.index).trimEnd() + 
          (content.slice(formMatch.index + formMatch[0].length).trimStart() ? "\n\n" + content.slice(formMatch.index + formMatch[0].length).trimStart() : "");
        return { body: cleanBody, form: parsed as ClarificationFormData };
      }
    } catch (e) {
      // JSON incompleto o no parseable aún
    }
  }
  return { body: content, form: null };
}

// Patterns that common AI models use for follow-up questions (markdown headings, "7. Preguntas...", bold, etc.)
function parseQuestionsFromContent(content: string): { questions: string[]; body: string } {
  if (!content?.trim()) return { questions: [], body: content };

  const headerRe =
    /(?:^|[\r\n])\s*(?:#{1,6}\s+)?(?:[*_]{1,2})?(?:\d+[.)]\s*)?(?:[*_]{1,2})?(Preguntas\s+(?:de\s+)?(?:seguimiento|relacionadas|sugeridas)(?:[^\r\n:]*)?|questions|actions|Siguientes\s+pasos|Acciones\s+recomendadas|También\s+puedes\s+preguntar)\s*(?:[*_]{1,2})?\s*:?\s*(?:\r?\n|$)/i;

  const m = headerRe.exec(content);
  if (!m || m.index === undefined) return { questions: [], body: content };

  const startIndex = m.index;
  const headerFullLen = m[0].length;
  const body = content.slice(0, startIndex).trimEnd();
  let questionsPart = content.slice(startIndex + headerFullLen).trimStart();

  const lines = questionsPart.split(/\r?\n/);
  const questions: string[] = [];
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;
    if (!/\?/.test(line)) continue;
    const cleaned = line
      .replace(/^\s*(?:\d+[.)]\s+|[*•\-–—]\s+|>+\s*)/u, "")
      .replace(/\*+|_+/g, "")
      .trim();
    if (cleaned.length > 10) questions.push(cleaned);
  }

  return { questions: Array.from(new Set(questions)).slice(0, 3), body: body.trimEnd() };
}

// Parse sources from assistant markdown when backend doesn't return structured references
function parseSourcesFromContent(
  content: string
): { sources: string[]; citations: string[]; startIndex: number; items?: { name: string; citation: string; index: number; pageNumber?: number; highlight_text?: string }[] } | null {
  const headingRegex = /(?:^|\n)\s*(?:#{1,6}\s*)?(?:[*_]{1,2})?(?:\d+[.)]\s*)?(?:[*_]{1,2})?\s*\bFuentes(?:\s+(?:consultadas|citadas))?\b\s*(?:[*_]{1,2})?\s*:?/i;
  const headingMatch = content.match(headingRegex);
  if (!headingMatch || headingMatch.index === undefined) return null;
  const startIndex = headingMatch.index;
  const afterHeadingIdx = headingMatch.index + headingMatch[0].length;
  const section = content.slice(afterHeadingIdx);
  // Consider until next top-level heading, divider, or end
  const nextHeadingIdx = section.search(/\n\s*(?:#{1,3}\s|\bAviso\s+legal\b|\*\*⚠️|⚠️|---|___|\*\*\*)/i);
  const body =
    nextHeadingIdx > -1 ? section.slice(0, nextHeadingIdx) : section;

  const sourcesMap = new Map<number, { name: string; citation: string; pageNumber?: number }>();
  const unindexedSources: { name: string; citation: string; pageNumber?: number }[] = [];

  const lines = body.split(/\n+/);
  const linkRegex = /\[([^\]]+)\]\((https?:[^)\s]+)\)/g;

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;

    // Check if line is the heading itself
    if (line.match(/^(?:#{1,4}|\*\*|\*|__)?\s*Fuentes(?:\s+(?:consultadas|citadas))?\s*:?\s*(?:\*\*|\*|__)?$/i)) {
      continue;
    }

    const pageMatch = line.match(/p(?:[áa]g(?:s|\.|\.s)?)\s*[:.]?\s*(\d+)/i);
    const pageNumber = pageMatch ? parseInt(pageMatch[1], 10) : undefined;

    let matchedAny = false;
    let m: RegExpExecArray | null;
    while ((m = linkRegex.exec(line)) !== null) {
      matchedAny = true;
      const label = m[1].trim();
      unindexedSources.push({ name: label, citation: line, pageNumber });
    }

    if (!matchedAny) {
      // Check for bracketed or numbered item like '• [3] Sentencia C-038/20...' or '1. Ley 100...'
      const itemMatch = line.match(/^(?:[-*•]\s*)?(?:\[(\d+)\]|\b(\d+)[\.\)])\s*(.+)/);
      if (itemMatch) {
        const explicitIdx = parseInt(itemMatch[1] || itemMatch[2], 10);
        const textLabel = itemMatch[3].trim();
        sourcesMap.set(explicitIdx, { name: textLabel, citation: line, pageNumber });
        matchedAny = true;
      }
    }

    if (!matchedAny) {
      // Fallback: plain bullet item like '• Sentencia C-038/20...'
      const bulletMatch = line.match(/^(?:[-*•]\s+)(.+)/);
      if (bulletMatch) {
        unindexedSources.push({ name: bulletMatch[1].trim(), citation: line, pageNumber });
      }
    }
  }

  if (sourcesMap.size === 0 && unindexedSources.length === 0) return null;

  const items: { name: string; citation: string; index: number; pageNumber?: number; highlight_text?: string }[] = [];
  
  // Add indexed sources
  sourcesMap.forEach((val, idx) => {
    items.push({
      name: val.name,
      citation: val.citation,
      index: idx,
      pageNumber: val.pageNumber,
      highlight_text: val.name
    });
  });

  // Assign next available indices to unindexed sources
  let nextIdx = 1;
  while (sourcesMap.has(nextIdx)) nextIdx++;
  unindexedSources.forEach((un) => {
    while (sourcesMap.has(nextIdx)) nextIdx++;
    items.push({
      name: un.name,
      citation: un.citation,
      index: nextIdx,
      pageNumber: un.pageNumber,
      highlight_text: un.name
    });
    nextIdx++;
  });

  items.sort((a, b) => a.index - b.index);

  const sources = items.map((it) => it.name);
  const citations = items.map((it) => it.citation);

  return { sources, citations, startIndex, items };
}

// Type for chat history items
export type ChatHistoryItem = {
  id: string;
  user_id?: string;
  title: string;
  instructions?: string | null;
  date: string;
  messages: Message[];
};

export default function ChatLegalDashboard() {
  const { user } = useAuth();
  const apiClient = useApiClient();
  const flow = useFlowEngine();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlChatId = searchParams.get("chat_id");
  const { runs, activeRunId, startChatRun, cancelRun, getRun, updateRun } = useAgentExecution();
  const [message, setMessage] = useState("");
  // Local messages used for history or while not running
  const [localMessages, setLocalMessages] = useState<Message[]>([]);
  // Use a derived messages setter that handles both local and possibly context state
  const setMessages = (msgs: Message[] | ((prev: Message[]) => Message[])) => {
    setLocalMessages(msgs as any);
  };
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("chat");
  const [interactionMode, setInteractionMode] = useState<"general" | "estudiante" | "consultorio">("general");
  const [instructions, setInstructions] = useState(getAgentBySlug("legal-chatbot")?.system_prompt || "");
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [cloudflareFileKey, setCloudflareFileKey] = useState<string | null>(null);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [chatHistory, setChatHistory] = useState<ChatHistoryItem[]>([]);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<ChatHistoryItem | null>(null);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [vectorizing, setVectorizing] = useState(false);
  const [documentRagReady, setDocumentRagReady] = useState(false);
  const [showFiltersSidebar, setShowFiltersSidebar] = useState(false);
  const [showHistorySidebar, setShowHistorySidebar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);
  const [showInstructions, setShowInstructions] = useState(false);
  const [pendingDeleteHistoryItem, setPendingDeleteHistoryItem] = useState<ChatHistoryItem | null>(null);
  const [showDeleteHistoryModal, setShowDeleteHistoryModal] = useState(false);
  const [historyReloadTrigger, setHistoryReloadTrigger] = useState(0);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [editingMessageIndex, setEditingMessageIndex] = useState<number | null>(null);
  const [editingMessageContent, setEditingMessageContent] = useState<string>("");
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [isMobile]);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [showProcesoDialog, setShowProcesoDialog] = useState(false);
  const [procesos, setProcesos] = useState<ScrapperProceso[]>([]);
  const [selectedProceso, setSelectedProceso] = useState<ScrapperProceso | null>(null);
  const [loadingProcesos, setLoadingProcesos] = useState(false);
  const [isFlowModalOpen, setIsFlowModalOpen] = useState(false);
  const [isFlowLoading, setIsFlowLoading] = useState(false);
  const [historySearchQuery, setHistorySearchQuery] = useState("");
  const [isFocusedContext, setIsFocusedContext] = useState(false);
  const [lastUploadedDocUrl, setLastUploadedDocUrl] = useState<string | null>(null);
  const [storageUsage, setStorageUsage] = useState<any>(null);
  const [loadingStorage, setLoadingStorage] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const sidebarSearchRef = useRef<HTMLInputElement>(null);
  const [showOnlyPinned, setShowOnlyPinned] = useState(false);
  const [pinnedChatIds, setPinnedChatIds] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem("pinned_chats_sara");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const togglePinChat = (id: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setPinnedChatIds((prev) => {
      const isPinned = prev.includes(id);
      const next = isPinned ? prev.filter((item) => item !== id) : [...prev, id];
      try {
        localStorage.setItem("pinned_chats_sara", JSON.stringify(next));
      } catch (err) {
        console.error("Error saving pinned chats:", err);
      }
      toast.success(!isPinned ? "Consulta fijada 📌" : "Consulta desfijada");
      return next;
    });
  };

  const loadStorageUsage = async () => {
    setLoadingStorage(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL || "http://localhost:8001"}/api/v1/user/storage-usage`, {
        headers: {
          "Authorization": `Bearer ${token || ""}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setStorageUsage(data);
        window.dispatchEvent(new Event("storage-updated"));
      }
    } catch (error) {
      console.error("Error loading storage usage:", error);
    } finally {
      setLoadingStorage(false);
    }
  };

  // --- Highlighting State ---
  const [highlights, setHighlights] = useState<Record<number, Highlight[]>>({});
  const [selection, setSelection] = useState<{
    text: string;
    rect: DOMRect;
    index: number;
    toolbarLeft: number;
    toolbarTop: number;
  } | null>(null);
  const [quotedText, setQuotedText] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const assistantBubbleRefs = useRef<(HTMLDivElement | null)[]>([]);
  const messageRowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const messagesRef = useRef<{ role: string; content?: string }[]>([]);
  const assistantIndicesRef = useRef<number[]>([]);
  const assistantNavOrdinalRef = useRef(0);
  const prevAssistantCountRef = useRef(0);
  const isNavigatingRef = useRef(false);
  const navigationTimeoutRef = useRef<any>(null);
  const [assistantNavOrdinal, setAssistantNavOrdinal] = useState(0);

  const [isWebSearchEnabled, setIsWebSearchEnabled] = useState(false);
  const [isCodeEnabled, setIsCodeEnabled] = useState(false);
  const [isResearchEnabled, setIsResearchEnabled] = useState(true);
  const [showFeaturesMenu, setShowFeaturesMenu] = useState(false);
  const [skills, setSkills] = useState<any[]>([]);
  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(null);
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [showSkillsMenu, setShowSkillsMenu] = useState(false);
  const { isOpen, docTitle, docUrl, openDocument, registerActionHandler } = useDocumentViewer();
  const requiresIndexing = !!attachedFile && !!lastUploadedDocUrl && !!docUrl && docUrl === lastUploadedDocUrl;
  const isDocumentProcessing = requiresIndexing && (uploading || vectorizing || !documentRagReady);

  // Derived state from context
  const currentRun = conversationId ? getRun(conversationId) : null;

  // UNIFIED MESSAGE STATE: Merge Agent Execution runs with Local Flow state (memoized so deps stay stable)
  const messages = useMemo(() => {
    const runMsgs = currentRun?.messages || [];
    if (localMessages.length > 0 && runMsgs.length > 0) {
      return [...runMsgs, ...localMessages.slice(runMsgs.length)];
    }
    return runMsgs.length > 0 ? runMsgs : localMessages;
  }, [currentRun?.messages, localMessages]);
  const assistantIndices = useMemo(
    () =>
      messages
        .map((m, i) => (m.role === "assistant" ? i : -1))
        .filter((i) => i >= 0),
    [messages]
  );
  const assistantOrdinalForIndex = useMemo(() => {
    const map = new Map<number, number>();
    let ord = 0;
    messages.forEach((m, i) => {
      if (m.role === "assistant") map.set(i, ord++);
    });
    return map;
  }, [messages]);

  messagesRef.current = messages as { role: string; content?: string }[];
  assistantIndicesRef.current = assistantIndices;
  assistantNavOrdinalRef.current = assistantNavOrdinal;

  const lastMsgForRail = messages[messages.length - 1];
  const assistantRailLayoutKey = `${messages.length}:${lastMsgForRail?.content?.length ?? 0
    }`;

  const isCurrentlyRunning = currentRun?.status === "loading" || currentRun?.status === "streaming";
  const traceSteps = currentRun?.traceSteps || [];
  const currentMetadata = currentRun?.metadata || null;

  // Filtering history based on search query and pinned filter
  const filteredHistory = chatHistory.filter((item) => {
    if (showOnlyPinned && !pinnedChatIds.includes(item.id)) return false;
    if (!historySearchQuery.trim()) return true;
    const query = historySearchQuery.toLowerCase();
    const titleMatch = item.title.toLowerCase().includes(query);
    const contentMatch = item.messages.some((msg) =>
      msg.content.toLowerCase().includes(query)
    );
    return titleMatch || contentMatch;
  });

  const scrollToBottom = useCallback((force = false) => {
    if (!force && !isAtBottomRef.current) return;

    // Prefer scrolling the container; fallback to the anchor
    if (scrollContainerRef.current) {
      const el = scrollContainerRef.current;

      // During streaming, use "auto" (instant) to prevent smooth-scroll jitter 
      // and allow the user to more easily break the scroll by moving up.
      const isStreaming = messagesRef.current[messagesRef.current.length - 1]?.role === "assistant" &&
        currentRun?.status === "streaming";
      const behavior = (force || !isStreaming) ? "smooth" : "auto";

      // Use requestAnimationFrame to ensure layout has settled (markdown tables, images, etc.)
      requestAnimationFrame(() => {
        el.scrollTo({ top: el.scrollHeight, behavior });
      });
      return;
    }
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, []);

  const scrollAssistantOrdinalIntoView = useCallback((ordinal: number) => {
    isNavigatingRef.current = true;
    if (navigationTimeoutRef.current) clearTimeout(navigationTimeoutRef.current);

    const assistantIndices = assistantIndicesRef.current;
    const aiIdx = assistantIndices[ordinal];

    // Destino directo: la respuesta jurídica de SarAI correspondiente al ordinal
    const targetEl = (document.querySelector(`[data-assistant-ordinal="${ordinal}"]`) as HTMLElement)
      || assistantBubbleRefs.current[ordinal]
      || (aiIdx !== undefined ? (document.querySelector(`[data-message-index="${aiIdx}"]`) as HTMLElement) : null)
      || (aiIdx !== undefined ? messageRowRefs.current[aiIdx] : null);

    if (targetEl) {
      // Desplazamiento nativo del navegador (funciona sin importar el contenedor de scroll)
      targetEl.scrollIntoView({ behavior: "smooth", block: "start" });

      const container = scrollContainerRef.current;
      if (container) {
        const containerRect = container.getBoundingClientRect();
        const targetRect = targetEl.getBoundingClientRect();
        const offsetTop = targetRect.top - containerRect.top + container.scrollTop - 20;
        container.scrollTo({
          top: Math.max(0, offsetTop),
          behavior: "smooth",
        });
      }
    }

    navigationTimeoutRef.current = setTimeout(() => {
      isNavigatingRef.current = false;
    }, 1000);
  }, []);

  const navigateToAssistantOrdinal = useCallback((targetOrdinal: number) => {
    const total = assistantIndices.length;
    if (total === 0) return;
    const bounded = Math.max(0, Math.min(total - 1, targetOrdinal));
    assistantNavOrdinalRef.current = bounded;
    setAssistantNavOrdinal(bounded);
    scrollAssistantOrdinalIntoView(bounded);
  }, [assistantIndices.length, scrollAssistantOrdinalIntoView]);

  const handleContainerScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const threshold = 30;
    isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;

    if (isNavigatingRef.current) return;

    const assistantIndices = assistantIndicesRef.current;
    if (assistantIndices.length > 0) {
      const containerRect = el.getBoundingClientRect();
      const containerTopY = containerRect.top + 80;

      let foundOrdinal = -1;
      let closestOrdinal = 0;
      let minDistance = Infinity;

      for (let ord = 0; ord < assistantIndices.length; ord++) {
        const bubbleEl = (document.querySelector(`[data-assistant-ordinal="${ord}"]`) as HTMLElement)
          || assistantBubbleRefs.current[ord];
        if (bubbleEl) {
          const rect = bubbleEl.getBoundingClientRect();
          if (rect.top <= containerTopY + 60 && rect.bottom > containerTopY) {
            foundOrdinal = ord;
            break;
          }
          const distance = Math.abs(rect.top - containerTopY);
          if (distance < minDistance) {
            minDistance = distance;
            closestOrdinal = ord;
          }
        }
      }

      const activeOrdinal = foundOrdinal !== -1 ? foundOrdinal : closestOrdinal;
      if (assistantNavOrdinalRef.current !== activeOrdinal) {
        assistantNavOrdinalRef.current = activeOrdinal;
        setAssistantNavOrdinal(activeOrdinal);
      }
    }
  }, []);

  // Top-anchor scroll: Position viewport at the start of the response when generated
  const lastScrolledAssistantIndexRef = useRef<number | null>(null);

  useEffect(() => {
    if (!messages || messages.length === 0) return;
    const lastIdx = messages.length - 1;
    const lastMsg = messages[lastIdx];

    // Automatically scroll to bottom when user enters a question
    if (lastMsg?.role === "user") {
      isAtBottomRef.current = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const container = scrollContainerRef.current;
          if (container) {
            container.scrollTo({
              top: container.scrollHeight,
              behavior: "smooth"
            });
          } else if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
          }
        });
      });
      return;
    }

    if (lastMsg?.role === "assistant" && lastScrolledAssistantIndexRef.current !== lastIdx) {
      lastScrolledAssistantIndexRef.current = lastIdx;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const container = scrollContainerRef.current;
          // Position at the user query immediately preceding the assistant response, or the assistant bubble
          const userMsgIdx = lastIdx > 0 && messages[lastIdx - 1]?.role === "user" ? lastIdx - 1 : lastIdx;
          const targetEl = messageRowRefs.current[userMsgIdx] || messageRowRefs.current[lastIdx];

          if (container && targetEl) {
            const containerRect = container.getBoundingClientRect();
            const targetRect = targetEl.getBoundingClientRect();
            const offsetTop = targetRect.top - containerRect.top + container.scrollTop - 20;

            container.scrollTo({
              top: Math.max(0, offsetTop),
              behavior: "smooth"
            });
          }
        });
      });
    }
  }, [messages.length, messages]);

  // Dynamically resize textarea height based on user input content
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const newHeight = Math.min(Math.max(el.scrollHeight, 38), 180);
    el.style.height = `${newHeight}px`;
  }, [message]);

  const fetchHighlightsForConversation = async (convId: string) => {
    try {
      const { data, error } = await supabase
        .from("chat_message_highlights")
        .select("*")
        .eq("conversation_id", convId);

      if (error) throw error;

      const hlByMsg: Record<number, Highlight[]> = {};
      (data || []).forEach((hl: { message_index: number } & Highlight) => {
        const idx =
          typeof hl.message_index === "number"
            ? hl.message_index
            : parseInt(String(hl.message_index), 10);
        if (!Number.isFinite(idx)) return;
        if (!hlByMsg[idx]) hlByMsg[idx] = [];
        hlByMsg[idx].push(hl);
      });
      setHighlights(hlByMsg);
    } catch (err) {
      console.error("Error fetching highlights:", err);
      setHighlights({});
    }
  };

  useEffect(() => {
    if (conversationId) {
      fetchHighlightsForConversation(conversationId);
    } else {
      setHighlights({});
    }
  }, [conversationId]);

  const saveHighlight = async (text: string, color: string, index: number) => {
    if (!user) {
      toast.error("Inicia sesión para guardar resaltados");
      return;
    }
    if (!conversationId) {
      toast.error("Envía un mensaje primero para activar la conversación y poder guardar resaltados");
      return;
    }

    try {
      await supabase
        .from("chat_message_highlights")
        .delete()
        .eq("conversation_id", conversationId)
        .eq("message_index", index)
        .eq("highlighted_text", text)
        .eq("user_id", user.id);

      const { data, error } = await supabase
        .from("chat_message_highlights")
        .insert({
          conversation_id: conversationId,
          message_index: index,
          highlighted_text: text,
          color,
          user_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      const row = data;
      if (!row) throw new Error("No row returned from highlight insert");

      setHighlights((prev) => {
        const list = [...(prev[index] || [])].filter(
          (h) => String(h.highlighted_text || h.text || "") !== text
        );
        list.push(row as Highlight);
        return { ...prev, [index]: list };
      });
      setSelection(null);
      window.getSelection()?.removeAllRanges();
      toast.success("Resaltado guardado");
    } catch (err) {
      console.error("Error saving highlight:", err);
      toast.error("Error al guardar el resaltado");
    }
  };

  const handleMouseUp = useCallback(() => {
    const root = scrollContainerRef.current;
    if (!root) return;

    const sel = window.getSelection();
    const selectedText = sel?.toString().trim() ?? "";

    if (!sel || sel.rangeCount === 0 || selectedText.length < 1) {
      setTimeout(() => {
        if (!window.getSelection()?.toString().trim()) {
          setSelection(null);
        }
      }, 100);
      return;
    }

    const range = sel.getRangeAt(0);
    if (range.collapsed) {
      setTimeout(() => {
        if (!window.getSelection()?.toString().trim()) {
          setSelection(null);
        }
      }, 100);
      return;
    }

    let rect = range.getBoundingClientRect();
    if (rect.width < 1 && rect.height < 1) {
      const rects = range.getClientRects();
      if (rects.length > 0) {
        rect = rects[0] as DOMRect;
      } else {
        return;
      }
    }

    const anchor = range.commonAncestorContainer;
    const anchorEl =
      anchor.nodeType === Node.ELEMENT_NODE ? (anchor as Element) : anchor.parentElement;
    if (!anchorEl || !root.contains(anchorEl)) return;

    // Use the selection range, not event.target — mouseup often fires outside the scroll area
    // while the selection is still inside the message.
    const messageContainer = anchorEl.closest(".chat-message-bubble");
    if (!messageContainer || !root.contains(messageContainer)) return;

    const bubbles = Array.from(root.querySelectorAll(".chat-message-bubble"));
    const index = bubbles.indexOf(messageContainer as HTMLElement);

    if (index !== -1) {
      setSelection({
        text: selectedText,
        rect,
        index,
        toolbarLeft: rect.left + rect.width / 2,
        toolbarTop: rect.top,
      });
    }
  }, []);

  const handleContinueWithSelection = () => {
    if (!selection) return;
    setQuotedText(selection.text);
    setSelection(null);

    // Focus the appropriate textarea
    setTimeout(() => {
      const el = textareaRef.current;
      if (el) {
        el.focus();
      }
    }, 50);
  };


  const fetchSpecificHistory = async (id: string) => {
    if (!user) return;
    const { data: sessionData, error: sessionError } = await supabase
      .from("chat_sessions")
      .select("id, user_id, title, created_at")
      .eq("id", id)
      .single();
    if (sessionData && !sessionError) {
      let messagesData: any[] = [];
      const { data: fullData, error: fetchErr } = await supabase
        .from("chat_messages")
        .select("role, content, created_at, sources, metadata")
        .eq("session_id", id)
        .order("created_at", { ascending: true });

      if (fetchErr && fetchErr.message?.includes("sources")) {
        const { data: fallbackData } = await supabase
          .from("chat_messages")
          .select("role, content, created_at")
          .eq("session_id", id)
          .order("created_at", { ascending: true });
        messagesData = fallbackData || [];
      } else {
        messagesData = fullData || [];
      }

      setConversationId(sessionData.id);

      const mappedMessages: Message[] = messagesData.map((m: any) => ({
        id: m.id,
        role: m.role as "user" | "assistant" | "system",
        content: m.content,
        timestamp: m.created_at,
        sourceDetails: m.sources || m.metadata?.source_details || [],
        sources: (m.sources || []).map((s: any) => typeof s === 'string' ? s : s.name),
        metadata: m.metadata || {},
        relatedQuestions: m.metadata?.contextual_questions || [],
      }));
      setLocalMessages(mappedMessages);
      setInstructions(""); // Legacy parameter
      await fetchHighlightsForConversation(sessionData.id);
    }
  };
  const fetchHistory = useCallback(async () => {
    if (!user) return;
    let rawSessions = null;
    const { data: fullSessions, error: fullErr } = await supabase
      .from("chat_sessions")
      .select("*, chat_messages(id, role, content, created_at, sources, metadata)")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(12);

    if (fullErr && fullErr.message?.includes("sources")) {
      const { data: fallbackSessions } = await supabase
        .from("chat_sessions")
        .select("*, chat_messages(id, role, content, created_at)")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(12);
      rawSessions = fallbackSessions;
    } else {
      rawSessions = fullSessions;
    }

    if (!rawSessions || rawSessions.length === 0) {
      setChatHistory([]);
    } else {
      // --- Map to ChatHistoryItem interface and inner sort ---
      const mappedHistory = rawSessions.map((session: any) => {
        // Sort inner messages ascending so chat flow is correct
        const msgs = session.chat_messages || [];
        msgs.sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

        return {
          id: session.id,
          user_id: session.user_id,
          title: session.title,
          date: session.created_at,
          messages: msgs.map((m: any) => ({
            id: m.id,
            role: m.role as "user" | "assistant" | "system",
            content: m.content,
            timestamp: m.created_at,
            sourceDetails: m.sources || m.metadata?.source_details || [],
            sources: (m.sources || []).map((s: any) => typeof s === 'string' ? s : s.name),
            metadata: m.metadata || {},
            relatedQuestions: m.metadata?.contextual_questions || [],
          })),
        };
      });

      setChatHistory(mappedHistory);
    }
  }, [user, setChatHistory]);

  const fetchSkills = useCallback(async () => {
    if (!user) return;
    setLoadingSkills(true);
    try {
      const { data, error } = await supabase
        .from("playbook_skills")
        .select("*")
        .or(`agent_type.eq.chatbot,agent_type.is.null`);

      if (data) {
        // Filter those that might be generically labeled or explicitly chatbot
        const chatbotSkills = data.filter((s: any) =>
          s.agent_type === 'chatbot' ||
          (s.description && s.description.includes('[CHATBOT]'))
        );
        setSkills(chatbotSkills);
      }
    } catch (err) {
      console.error("Error fetching skills:", err);
    } finally {
      setLoadingSkills(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSkills();
  }, [fetchSkills]);


  // --- Premium ChatGPT-style CSS ---
  const premiumStyles = (
    <style>{`
      .chat-sidebar {
        background: #f8fafc;
        border-right: 1px solid #e2e8f0;
        scrollbar-width: thin;
        scrollbar-color: #cbd5e1 transparent;
      }
      .chat-sidebar::-webkit-scrollbar { width: 4px; }
      .chat-sidebar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
      .chat-history-item { transition: all 0.15s ease; }
      .chat-history-item:hover { background: #f1f5f9; }
      .chat-history-item.active { background: #eff6ff; border-left: 2px solid #2563eb; }
      .msg-assistant { border-bottom: 1px solid #f1f5f9; }
      .msg-assistant:hover { background: #fafbfc; }
      .msg-user { background: #f8fafc; border-bottom: 1px solid #e2e8f0; }
      .typing-cursor::after {
        content: '▌'; color: #3b82f6; font-weight: 400;
        animation: blink 0.7s step-end infinite;
      }
      @keyframes blink { 50% { opacity: 0; } }
      .pill-input {
        background: rgba(255,255,255,0.95);
        backdrop-filter: blur(12px);
        border: 1px solid #e2e8f0;
        box-shadow: 0 -4px 20px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.03);
        transition: border-color 0.2s, box-shadow 0.2s;
      }
      .pill-input:focus-within {
        border-color: #93c5fd;
        box-shadow: 0 -4px 20px rgba(59,130,246,0.08), 0 0 0 2px rgba(59,130,246,0.15);
      }
      .source-chip {
        transition: all 0.2s ease;
        cursor: pointer;
      }
      .source-chip:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      }
      .analysis-panel {
        animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
      }
      @keyframes slideUp {
        from { opacity: 0; transform: translateY(12px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .suggestion-card {
        transition: all 0.2s ease;
        cursor: pointer;
      }
      .suggestion-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 16px rgba(0,0,0,0.08);
        border-color: #93c5fd;
      }
      .animated-gradient-bg {
        background: linear-gradient(120deg, #e0e7ff, #f0fdfa, #fef9c3, #f0fdfa, #e0e7ff);
        background-size: 200% 200%;
        animation: gradientMove 4s ease-in-out infinite;
      }
      @keyframes gradientMove {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
      /* Robust text wrapping & horizontal overflow prevention */
      .chat-md-html,
      .chat-md-html * {
        overflow-wrap: anywhere !important;
        word-break: break-word !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
      }
      .chat-md-html a {
        word-break: break-all !important;
        overflow-wrap: anywhere !important;
      }
      .chat-md-html code {
        word-break: break-all !important;
        white-space: pre-wrap !important;
      }
      .chat-md-html pre {
        max-width: 100% !important;
        overflow-x: auto !important;
        white-space: pre-wrap !important;
        word-break: break-all !important;
      }
      .chat-md-html table {
        display: block !important;
        width: 100% !important;
        max-width: 100% !important;
        overflow-x: auto !important;
      }
      .chat-message-bubble {
        overflow-wrap: anywhere;
        word-break: break-word;
      }
      .inline-citation-anchor {
        position: relative;
        display: inline;
        font-weight: bold;
        color: #2563eb !important;
        text-decoration: underline !important;
        cursor: pointer;
        padding: 0 2px;
        word-break: break-all;
        overflow-wrap: anywhere;
        max-width: 100%;
      }
      .inline-citation-anchor:hover::after {
        content: attr(data-excerpt);
        position: absolute;
        bottom: 130%;
        left: 50%;
        transform: translateX(-50%);
        background-color: #0f172a;
        color: #f8fafc;
        padding: 10px 14px;
        border-radius: 12px;
        font-family: ui-sans-serif, system-ui, sans-serif;
        font-size: 12px;
        line-height: 1.5;
        font-weight: normal;
        width: min(280px, calc(100vw - 32px));
        max-width: calc(100vw - 32px);
        white-space: normal;
        word-wrap: break-word;
        box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.3);
        border: 1px solid #1e293b;
        z-index: 9999;
        pointer-events: none;
        opacity: 0;
        animation: tooltipFadeIn 0.2s forwards;
      }
      .inline-citation-anchor:hover::before {
        content: '';
        position: absolute;
        bottom: 110%;
        left: 50%;
        transform: translateX(-50%);
        border-width: 6px;
        border-style: solid;
        border-color: #0f172a transparent transparent transparent;
        z-index: 9999;
        pointer-events: none;
        opacity: 0;
        animation: tooltipFadeIn 0.2s forwards;
      }
      @keyframes tooltipFadeIn {
        to { opacity: 1; }
      }
    `}</style>
  );

  // Fetch last 12 chat history items for the user
  useEffect(() => {
    fetchHistory();
  }, [fetchHistory, historyReloadTrigger]);

  // Load chat from URL if present
  useEffect(() => {
    if (urlChatId && user && conversationId !== urlChatId) {
      fetchSpecificHistory(urlChatId);
    }
  }, [urlChatId, user]);

  // Load conversation from history
  const handleHistoryClick = async (item: ChatHistoryItem) => {
    lastScrolledAssistantIndexRef.current = null;
    setConversationId(item.id);
    setSearchParams({ chat_id: item.id }, { replace: true });
    setLocalMessages(item.messages);
    setInstructions(item.instructions || "");
    setActiveTab("chat");
    setShowAnalysis(false);
    if (isMobile) {
      setSidebarOpen(false);
    }

    // Load existing feedback for this conversation
    if (user && item.id) {
      await loadMessageFeedback(item.id, item.messages);
    }
    await fetchHighlightsForConversation(item.id);
  };

  // Load existing feedback for messages
  const loadMessageFeedback = async (convId: string, messages: Message[]) => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("chat_message_feedback")
        .select("message_index, feedback_type")
        .eq("user_id", user.id)
        .eq("conversation_id", convId)
        .eq("message_role", "assistant");

      if (error) {
        console.error("Error loading message feedback:", error);
        return;
      }

      if (data && data.length > 0) {
        // Update messages with existing feedback
        const updatedMessages = messages.map((msg, index) => {
          if (msg.role === "assistant") {
            const feedback = data.find((f) => f.message_index === index);
            return { ...msg, feedback: feedback?.feedback_type || null };
          }
          return msg;
        });
        setMessages(updatedMessages);
      }
    } catch (error) {
      console.error("Error loading message feedback:", error);
    }
  };

  const resetChat = () => {
    lastScrolledAssistantIndexRef.current = null;
    setConversationId(null);
    setSearchParams({}, { replace: true });
    setLocalMessages([]);
    setMessage("");
    setInstructions("");
    setAttachedFile(null);
    setCloudflareFileKey(null);
    setLastUploadedDocUrl(null);
    setDocumentRagReady(false);
    setShowAnalysis(false);
    setQuotedText(null);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const handleDeleteHistoryItem = async (itemToDelete: ChatHistoryItem) => {
    if (!user) return;
    setLoading(true);
    try {
      const { error } = await supabase
        .from("chat_sessions")
        .delete()
        .eq("id", itemToDelete.id)
        .eq("user_id", user.id);

      if (error) {
        throw new Error(error.message);
      }

      toast.success("Consulta eliminada correctamente.");
      // If the deleted item was the currently active conversation, reset the chat
      if (conversationId === itemToDelete.id) {
        resetChat();
      }
      setHistoryReloadTrigger((prev) => prev + 1); // Trigger history reload
      fetchHistory(); // Explicitly re-fetch history
    } catch (error) {
      console.error("Error deleting chat history item:", error);
      toast.error("Error al eliminar la consulta.");
    } finally {
      setLoading(false);
      setPendingDeleteHistoryItem(null);
    }
  };

  const handleVectorize = async (overrideFileKey?: string, overrideTitle?: string): Promise<boolean> => {
    const fileKey = overrideFileKey || cloudflareFileKey;
    const title = overrideTitle || attachedFile?.name;
    if (!fileKey || !title || vectorizing) return false;

    setVectorizing(true);
    try {
      // Ensure we have a conversation id so vectorized chunks can be scoped.
      const convId = conversationId || crypto.randomUUID();
      if (!conversationId) {
        setConversationId(convId);
        setSearchParams({ chat_id: convId }, { replace: true });
      }
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(`${API_BASE_URL}/api/v1/vectorize`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          file_key: fileKey,
          bucket_name: STORAGE_BUCKET,
          title,
          document_type: "Documento Legal",
          knowledge_type: "chatbot",
          conversation_id: convId
        })
      });

      if (!response.ok) {
        if (response.status === 404) {
          // Backend without /api/v1/vectorize support:
          // fallback to focused_document flow (ask/stream fetches document directly).
          setDocumentRagReady(true);
          toast.success("Documento listo para consulta directa en el visor.");
          return true;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Error al vectorizar");
      }

      setDocumentRagReady(true);
      toast.success("Documento enviado a vectorización. Ya puedes consultarlo en este chat.");
      return true;
    } catch (error) {
      console.error("Vectorize error:", error);
      setDocumentRagReady(false);
      toast.error(error instanceof Error ? error.message : "Error al vectorizar el documento.");
      return false;
    } finally {
      setVectorizing(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!user) return;
    if (storageUsage && storageUsage.percentage >= 100) {
      toast.error("Límite de Almacenamiento Superado (5 GB). No es posible subir archivos.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("El archivo no puede superar los 10 MB.");
      return;
    }

    setUploading(true);
    setDocumentRagReady(false);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      const { data, error } = await supabase.storage
        .from(STORAGE_BUCKET)
        .upload(filePath, file);

      if (error) throw error;

      const { data: publicData } = supabase.storage
        .from(STORAGE_BUCKET)
        .getPublicUrl(filePath);

      setAttachedFile(file);
      setCloudflareFileKey(data.path);

      if (publicData?.publicUrl) {
        openDocument(publicData.publicUrl, file.name);
        setIsFocusedContext(true);
        setLastUploadedDocUrl(publicData.publicUrl);
      }

      // Trigger indexing automatically. If backend doesn't support /vectorize,
      // handleVectorize falls back to direct-document mode and returns true.
      const ready = await handleVectorize(data.path, file.name);
      if (!ready) {
        toast.error("No se pudo preparar el documento para consulta.");
        return;
      }

      toast.success("Archivo subido y listo para consultas.");
      loadStorageUsage();
    } catch (err) {
      console.error("Supabase upload error:", err);
      toast.error("Error al subir el archivo a Supabase.");
    } finally {
      setUploading(false);
    }
  };


  const sendMessage = async (overrideMessage?: string, overrideFocusedDocument?: string | null) => {
    const messageToSend = overrideMessage || message;
    if ((!messageToSend.trim() && !attachedFile) || isCurrentlyRunning || uploading) return;
    if (isDocumentProcessing) {
      toast.error("Espera a que termine la carga e indexación del documento para continuar.");
      return;
    }
    const focusedDoc = overrideFocusedDocument !== undefined ? overrideFocusedDocument : (isOpen ? docUrl : null);
    if (focusedDoc && requiresIndexing && !documentRagReady) {
      toast.error("El documento aún no está listo para consultas. Espera a que finalice la indexación automática.");
      return;
    }

    const activeConvId = conversationId || crypto.randomUUID();
    if (!conversationId) {
      setConversationId(activeConvId);
      setSearchParams({ chat_id: activeConvId }, { replace: true });
    }

    const currentMessage = messageToSend;
    const finalContent = quotedText ? `Sobre este texto: "${quotedText}"\n\n${currentMessage}` : currentMessage;

    setMessage("");
    setQuotedText(null);
    setAttachedFile(null);
    setCloudflareFileKey(null);
    setShowAnalysis(false);

    // Force scroll to bottom for new interaction
    isAtBottomRef.current = true;
    scrollToBottom(true);

    try {
      await startChatRun(
        activeConvId,
        messages,
        finalContent,
        instructions,
        cloudflareFileKey,
        user?.id || null,
        apiClient,
        null,
        isWebSearchEnabled,
        focusedDoc,
        selectedSkillId,
        interactionMode
      );

      // Track usage for dashboard
      if (user) {
        UsageTracker.trackLegalChatbot(user.id, "Mensaje enviado", undefined, undefined);
      }

      // After completion, update history with a small delay to ensure backend persistence
      if (user) {
        setTimeout(() => {
          fetchHistory();
        }, 1000); // 1 second delay to allow for eventual consistency
      }
    } catch (error) {
      console.error("SendMessage error:", error);
    }
  };

  const handleEditMessage = (index: number, content: string) => {
    // Strip instructions if present
    const cleanContent = content.split("\n\n[Instrucciones para la IA:")[0];
    setEditingMessageIndex(index);
    setEditingMessageContent(cleanContent);
  };

  const cancelEdit = () => {
    setEditingMessageIndex(null);
    setEditingMessageContent("");
  };

  const submitMessageEdit = async () => {
    if (editingMessageIndex === null || !editingMessageContent.trim()) return;

    const index = editingMessageIndex;
    const newContent = editingMessageContent;

    // Reset editing state
    setEditingMessageIndex(null);
    setEditingMessageContent("");

    // Get messages up to the one being edited
    const messagesToKeep = messages.slice(0, index);
    setLocalMessages(messagesToKeep);

    // Trigger re-generation
    const activeConvId = conversationId;
    if (!activeConvId) return;

    try {
      // Delete from DB to ensure persistence is clean
      // We delete all messages from this index onwards for this session
      const { data: dbMessages } = await supabase
        .from("chat_messages")
        .select("id")
        .eq("session_id", activeConvId)
        .order("created_at", { ascending: true });

      if (dbMessages && dbMessages.length > index) {
        const idsToDelete = dbMessages.slice(index).map((m: any) => m.id);
        await supabase.from("chat_messages").delete().in("id", idsToDelete);
      }

      await startChatRun(
        activeConvId,
        messagesToKeep,
        newContent,
        instructions,
        null,
        user?.id || null,
        apiClient,
        null,
        isWebSearchEnabled,
        isOpen ? docUrl : null,
        selectedSkillId
      );

      // Track usage
      if (user) {
        UsageTracker.trackLegalChatbot(user.id, "Mensaje editado", undefined, undefined);
      }

      // After completion, update history with a small delay
      if (user) {
        setTimeout(() => {
          fetchHistory();
        }, 1000);
      }
    } catch (error) {
      console.error("Edit submission error:", error);
    }
  };

  const handleDocumentAction = (actionPrompt: string) => {
    if (docUrl) {
      if (requiresIndexing && !documentRagReady) {
        toast.error("El documento aún se está preparando. Espera a que finalice la indexación automática.");
        return;
      }
      setIsFocusedContext(true);
      const docContextPrefix = docTitle ? `Basándote en el documento "${docTitle}", ` : "Basándote en el documento abierto, ";
      const finalPrompt = `${docContextPrefix}${actionPrompt.toLowerCase()}`;
      sendMessage(finalPrompt, docUrl);
    } else {
      sendMessage(actionPrompt);
    }
  };

  useEffect(() => {
    if (registerActionHandler) {
      registerActionHandler(handleDocumentAction);
    }
  }, [registerActionHandler, handleDocumentAction]);


  const copyToClipboard = async (content: string, messageId: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      toast.success("Contenido copiado al portapapeles");

      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedMessageId(null);
      }, 2000);
    } catch (err) {
      console.error("Error copying to clipboard:", err);
      toast.error("Error al copiar el contenido");
    }
  };

  const handleMessageFeedback = async (
    messageIndex: number,
    feedback?: "thumbs_up" | "thumbs_down" | null
  ) => {
    if (!user || !conversationId) {
      toast.error("No se puede guardar el feedback sin usuario o conversación");
      return;
    }

    const msg = messages[messageIndex];
    if (!msg) {
      toast.error("Error al guardar el feedback: Mensaje no encontrado");
      return;
    }

    try {
      // Check if feedback already exists in chat_message_feedback table
      const { data: existingFeedback, error: checkError } = await supabase
        .from("chat_message_feedback")
        .select("id")
        .eq("user_id", user.id)
        .eq("conversation_id", conversationId)
        .eq("message_index", messageIndex)
        .maybeSingle();

      if (checkError) {
        console.error("Error checking existing feedback:", checkError);
      }

      let query;
      if (existingFeedback) {
        const updatePayload: any = {
          feedback_type: feedback,
          updated_at: new Date().toISOString(),
        };
        if (msg.id) {
          updatePayload.message_id = msg.id;
        }

        query = supabase
          .from("chat_message_feedback")
          .update(updatePayload)
          .eq("id", existingFeedback.id);
      } else {
        const insertPayload: any = {
          user_id: user.id,
          conversation_id: conversationId,
          message_index: messageIndex,
          message_role: msg.role,
          feedback_type: feedback,
        };
        if (msg.id) {
          insertPayload.message_id = msg.id;
        }

        query = supabase
          .from("chat_message_feedback")
          .insert(insertPayload);
      }

      const { error } = await query;
      if (error) throw error;

      // Update local state
      setLocalMessages((prevMessages) =>
        prevMessages.map((m, idx) =>
          idx === messageIndex ? { ...m, feedback: feedback } : m
        )
      );

      // Also update agent execution run messages if active
      if (conversationId && runs[conversationId]) {
        const runMessages = runs[conversationId].messages || [];
        const updatedRunMessages = runMessages.map((m, idx) =>
          idx === messageIndex ? { ...m, feedback: feedback } : m
        );
        updateRun(conversationId, { messages: updatedRunMessages });
      }

      toast.success(
        feedback === "thumbs_up"
          ? "¡Gracias por tu feedback positivo!"
          : "Gracias por tu feedback. Nos ayuda a mejorar."
      );
    } catch (error) {
      console.error("Error saving message feedback:", error);
      toast.error("Error al guardar el feedback");
    }
  };

  // Helper functions removed from here (duplicates)

  const handleDownloadPDF = async (content: string, title: string) => {
    try {
      const { body: cleanDocument } = extractDocumentContent(content);
      const doc = new jsPDF();
      const margin = 20; // Professional margins (approx 2cm)
      const pageWidth = doc.internal.pageSize.getWidth();
      const textWidth = pageWidth - 2 * margin;
      let cursorY = 20;

      // Professional Header
      doc.setFillColor(37, 99, 235); // Blue-600
      doc.rect(0, 0, pageWidth, 5, "F");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(100, 116, 139); // Slate-500
      doc.text("SarAI — INTELIGENCIA JURÍDICA", margin, cursorY);

      doc.setFontSize(8);
      doc.text(`GENERADO EL ${new Date().toLocaleDateString('es-CO').toUpperCase()}`, pageWidth - margin - 40, cursorY);

      cursorY += 15;

      // Document Title
      doc.setFontSize(16);
      doc.setTextColor(30, 41, 59); // Slate-800
      const titleLines = doc.splitTextToSize(title.toUpperCase(), textWidth);
      doc.text(titleLines, margin, cursorY);
      cursorY += (titleLines.length * 7) + 5;

      // Divider
      doc.setDrawColor(226, 232, 240); // Slate-200
      doc.line(margin, cursorY, pageWidth - margin, cursorY);
      cursorY += 10;

      // Content Processing
      doc.setFont("times", "normal"); // Professional serif for legal
      doc.setFontSize(11);
      doc.setTextColor(51, 65, 85); // Slate-700

      // Helper to clean all markdown syntax for professional PDF text
      const cleanMarkdown = (rawText: string): string => {
        return rawText
          .replace(/\*\*\*(.*?)\*\*\*/g, '$1') // Bold & Italic
          .replace(/\*\*(.*?)\*\*/g, '$1')    // Bold
          .replace(/\*(.*?)\*/g, '$1')        // Italic
          .replace(/__(.*?)__/g, '$1')        // Bold
          .replace(/_(.*?)_/g, '$1')          // Italic
          .replace(/`(.*?)`/g, '$1')          // Inline Code
          .replace(/\[(.*?)\]\(.*?\)/g, '$1') // Markdown Links
          .replace(/\\/g, '');                // Backslashes
      };

      const lines = cleanDocument.split('\n');
      for (const line of lines) {
        if (cursorY > 270) {
          doc.addPage();
          cursorY = 20;
          doc.setFillColor(37, 99, 235);
          doc.rect(0, 0, pageWidth, 5, "F");
        }

        let text = line.trim();
        if (!text) {
          cursorY += 5;
          continue;
        }

        // Headers
        if (text.startsWith('#')) {
          doc.setFont("helvetica", "bold");
          const level = (text.match(/^#+/) || [''])[0].length;
          const fontSize = level === 1 ? 14 : level === 2 ? 12 : 11;
          doc.setFontSize(fontSize);
          text = cleanMarkdown(text.replace(/^#+\s*/, '')).toUpperCase();
          const wrapped = doc.splitTextToSize(text, textWidth);
          doc.text(wrapped, margin, cursorY);
          cursorY += (wrapped.length * (fontSize * 0.5)) + 4;
          doc.setFont("times", "normal");
          doc.setFontSize(11);
        }
        // Bullet points
        else if (text.startsWith('-') || text.startsWith('*')) {
          const rawItem = text.substring(1).trim();
          const cleanedItem = "• " + cleanMarkdown(rawItem);
          const wrapped = doc.splitTextToSize(cleanedItem, textWidth - 6);
          doc.text(wrapped, margin + 6, cursorY);
          cursorY += (wrapped.length * 6) + 2;
        }
        // Numbered list items
        else if (/^\d+[.)]\s/.test(text)) {
          const cleanedItem = cleanMarkdown(text);
          const wrapped = doc.splitTextToSize(cleanedItem, textWidth - 6);
          doc.text(wrapped, margin + 6, cursorY);
          cursorY += (wrapped.length * 6) + 2;
        }
        // Normal text paragraph
        else {
          const cleanedText = cleanMarkdown(text);
          const wrapped = doc.splitTextToSize(cleanedText, textWidth);
          doc.text(wrapped, margin, cursorY);
          cursorY += (wrapped.length * 6) + 2;
        }
      }

      const date = new Date().toISOString().split('T')[0];
      const intent = title.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      doc.save(`${intent}_${date}_sara_ai.pdf`);
      toast.success("Documento descargado en formato legal (PDF)");
    } catch (err) {
      console.error("Error generating PDF:", err);
      toast.error("Error al generar el PDF profesional");
    }
  };

  const handleDownloadDOCX = async (content: string, title: string) => {
    try {
      const { body: cleanDocument } = extractDocumentContent(content);
      const htmlString = `
        <!DOCTYPE html>
        <html lang="es">
        <head>
          <meta charset="utf-8">
          <style>
            @page { margin: 2.5cm; }
            body { 
              font-family: 'Times New Roman', Times, serif; 
              line-height: 1.5; 
              color: #1a1a1a;
              text-align: justify;
            }
            .header {
              text-align: right;
              font-family: sans-serif;
              font-size: 9pt;
              color: #666;
              border-bottom: 2px solid #2563eb;
              padding-bottom: 5pt;
              margin-bottom: 20pt;
            }
            h1 { 
              color: #000; 
              font-size: 16pt; 
              text-align: center;
              text-transform: uppercase;
              margin-bottom: 20pt;
            }
            h2 { font-size: 14pt; margin-top: 15pt; }
            h3 { font-size: 12pt; margin-top: 12pt; }
            p { margin-bottom: 10pt; text-indent: 0pt; }
            ul, ol { margin-bottom: 15pt; margin-left: 20pt; }
            li { margin-bottom: 5pt; }
            strong { font-weight: bold; }
            .footer {
              margin-top: 50pt;
              font-size: 9pt;
              text-align: center;
              color: #999;
              font-style: italic;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <strong>SarAI — Asistente Jurídico</strong><br>
            FECHA DE GENERACIÓN: ${new Date().toLocaleDateString('es-CO')}
          </div>
          <h1>${title}</h1>
          ${marked.parse(cleanDocument)}
          <div class="footer">
            Este documento fue generado por SarAI — Inteligencia Artificial especializada en Derecho Colombiano.
          </div>
        </body>
        </html>
      `;

      const blob = await asBlob(htmlString);
      const date = new Date().toISOString().split('T')[0];
      const intent = title.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      saveAs(blob as Blob, `${intent}_${date}.docx`);
      toast.success("Documento Word generado con éxito");
    } catch (err) {
      console.error("Error generating DOCX:", err);
      toast.error("Error al generar el documento Word");
    }
  };

  const handleTopicSelect = (topic: string) => {
    setMessage(topic);
    setActiveTab("chat");
  };

  const handleSuggestionSelect = (query: string) => {
    setActiveTab("chat");
    void sendMessage(query);
  };

  useEffect(() => {
    if (assistantIndices.length === 0) {
      setAssistantNavOrdinal(0);
      prevAssistantCountRef.current = 0;
    }
  }, [assistantIndices.length]);

  useEffect(() => {
    const n = assistantIndices.length;
    if (n === 0) return;
    setAssistantNavOrdinal((prev) => Math.min(prev, n - 1));
  }, [assistantIndices.length]);

  useEffect(() => {
    const n = assistantIndices.length;
    if (n > prevAssistantCountRef.current && n > 1) {
      setAssistantNavOrdinal(n - 1);
    }
    prevAssistantCountRef.current = n;
  }, [assistantIndices.length]);

  // Assistant navigation is handled directly by navigateToAssistantOrdinal on click
  // and by handleContainerScroll on scroll-spy

  // --- FiltersSidebar component ---
  function FiltersSidebar({
    open,
    onClose,
  }: {
    open: boolean;
    onClose: () => void;
  }) {
    const [areaSearch, setAreaSearch] = useState("");
    const [selectedAreas, setSelectedAreas] = useState<string[]>([
      "Todas las categorías",
    ]);
    const legalAreas = [
      "Administrativo",
      "Aduanero",
      "Ambiental",
      "Civil",
      "Comercial",
      "Constitucional",
      "Cumplimiento",
      "Deporte",
      "Desastres Naturales",
      "Disciplinario",
    ];
    const filteredAreas = legalAreas.filter((area) =>
      area.toLowerCase().includes(areaSearch.toLowerCase())
    );
    // Handle select all
    const handleAreaChange = (area: string) => {
      if (area === "Todas las categorías") {
        if (selectedAreas.includes("Todas las categorías")) {
          setSelectedAreas([]);
        } else {
          setSelectedAreas(["Todas las categorías"]);
        }
      } else {
        let newSelected = selectedAreas.filter(
          (a) => a !== "Todas las categorías"
        );
        if (selectedAreas.includes(area)) {
          newSelected = newSelected.filter((a) => a !== area);
        } else {
          newSelected = [...newSelected, area];
        }
        setSelectedAreas(newSelected);
      }
    };
    return open ? (
      <div className="fixed inset-0 z-50 flex">
        {/* Overlay */}
        <div
          className="fixed inset-0 bg-black/20 transition-opacity"
          onClick={onClose}
        />
        {/* Sidebar */}
        <aside className="relative ml-auto w-full max-w-sm bg-white h-full shadow-xl rounded-l-2xl flex flex-col p-6 animate-slide-in-right overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Controles de consulta</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100">
              <X className="h-5 w-5" />
            </button>
          </div>
          <hr className="mb-4" />
          {/* Instrucciones */}
          <div className="mb-6">
            <h3 className="text-base font-bold mb-2">Instrucciones</h3>
            <textarea
              className="w-full min-h-[60px] rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 resize-none"
              placeholder="Agrega instrucciones específicas para la IA..."
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
            />
          </div>
          {/* Ãrea del derecho */}
          <div className="mb-2">
            <details open className="group">
              <summary className="flex items-center justify-between px-0 py-3 cursor-pointer select-none group-open:bg-gray-50 text-lg font-semibold">
                <span>Ãrea del derecho</span>
                <ChevronRight className="h-4 w-4 group-open:rotate-90 transition-transform" />
              </summary>
              <div className="px-0 pb-3">
                <input
                  type="text"
                  className="w-full mb-2 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder="Buscar"
                  value={areaSearch}
                  onChange={(e) => setAreaSearch(e.target.value)}
                />
                <div className="space-y-2">
                  <label
                    className={`flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer ${selectedAreas.includes("Todas las categorÃ­as")
                      ? "bg-blue-50"
                      : ""
                      }`}>
                    <input
                      type="checkbox"
                      checked={selectedAreas.includes("Todas las categorías")}
                      onChange={() => handleAreaChange("Todas las categorías")}
                      className="accent-blue-600 h-5 w-5 rounded border-gray-300"
                    />
                    <span
                      className={`font-medium ${selectedAreas.includes("Todas las categorías")
                        ? "text-blue-700"
                        : ""
                        }`}>
                      Todas las categorías
                    </span>
                  </label>
                  {filteredAreas.map((area) => (
                    <label
                      key={area}
                      className="flex items-center gap-2 px-2 py-1 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedAreas.includes(area)}
                        onChange={() => handleAreaChange(area)}
                        className="accent-blue-600 h-5 w-5 rounded border-gray-300"
                        disabled={selectedAreas.includes(
                          "Todas las categorÃ­as"
                        )}
                      />
                      <span>{area}</span>
                    </label>
                  ))}
                </div>
              </div>
            </details>
          </div>
        </aside>
      </div>
    ) : null;
  }

  // Add HistorySidebar component
  function HistorySidebar({
    open,
    onClose,
  }: {
    open: boolean;
    onClose: () => void;
  }) {
    return open ? (
      <div className="fixed inset-0 z-50 flex">
        {/* Overlay */}
        <div
          className="fixed inset-0 bg-black/20 transition-opacity"
          onClick={onClose}
        />
        {/* Sidebar */}
        <aside className="relative ml-auto w-full max-w-sm bg-white h-full shadow-xl rounded-l-2xl flex flex-col p-6 animate-slide-in-right overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Historial de consultas</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100">
              <X className="h-5 w-5" />
            </button>
          </div>
          <hr className="mb-4" />

          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar en el historial..."
              className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-9 py-3 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
              value={historySearchQuery}
              onChange={(e) => setHistorySearchQuery(e.target.value)}
            />
            {historySearchQuery && (
              <button
                onClick={() => setHistorySearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-slate-200 text-slate-400 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>

          <div className="space-y-4">
            {loading ? (
              <div className="text-center py-8 text-gray-500">
                Cargando historial...
              </div>
            ) : chatHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No hay consultas recientes.
              </div>
            ) : filteredHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {historySearchQuery ? "No se encontraron resultados." : "No hay consultas recientes."}
              </div>
            ) : (
              filteredHistory.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50 cursor-pointer transition-colors">
                  <div
                    onClick={() => {
                      handleHistoryClick(item);
                      onClose();
                    }}
                    className="flex-1 pr-2">
                    <p className="font-medium text-sm">{item.title}</p>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                      <Clock className="h-3 w-3" />
                      {new Date(item.date).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon">
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent triggering handleHistoryClick
                        setPendingDeleteHistoryItem(item);
                        setShowDeleteHistoryModal(true);
                      }}>
                      <Trash className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </aside>
      </div>
    ) : null;
  }

  // Helper: group history by date with pinned section at top
  const groupHistoryByDate = (items: ChatHistoryItem[]) => {
    const pinnedItems = items.filter((item) => pinnedChatIds.includes(item.id));
    const unpinnedItems = items.filter((item) => !pinnedChatIds.includes(item.id));

    const today = new Date(); today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today); weekAgo.setDate(weekAgo.getDate() - 7);

    const result: { label: string; isPinned?: boolean; items: ChatHistoryItem[] }[] = [];

    if (pinnedItems.length > 0) {
      result.push({ label: "Fijados 📌", isPinned: true, items: pinnedItems });
    }

    const dateGroups: { label: string; items: ChatHistoryItem[] }[] = [
      { label: "Hoy", items: [] },
      { label: "Ayer", items: [] },
      { label: "Esta semana", items: [] },
      { label: "Anteriores", items: [] },
    ];

    unpinnedItems.forEach((item) => {
      const d = new Date(item.date); d.setHours(0, 0, 0, 0);
      if (d >= today) dateGroups[0].items.push(item);
      else if (d >= yesterday) dateGroups[1].items.push(item);
      else if (d >= weekAgo) dateGroups[2].items.push(item);
      else dateGroups[3].items.push(item);
    });

    return [...result, ...dateGroups.filter((g) => g.items.length > 0)];
  };

  const renderSearchInput = () => (
    <div className="w-full relative">
      {/* Quoted text banner if user highlighted text to reply */}
      {quotedText && (
        <div className="mb-2 p-2.5 bg-blue-50/90 border border-blue-200/80 rounded-2xl flex items-start gap-3 relative animate-in slide-in-from-bottom-2 duration-200 pr-9">
          <div className="mt-0.5 text-blue-600">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 14l-4-4 4-4" /><path d="M5 10h11a4 4 0 1 1 0 8h-1" /></svg>
          </div>
          <div className="flex-1 text-xs text-blue-900 line-clamp-2 leading-relaxed italic font-medium">
            "{quotedText}"
          </div>
          <button
            onClick={() => setQuotedText(null)}
            className="absolute top-2 right-2 p-1 rounded-lg hover:bg-blue-100/60 text-blue-500 hover:text-blue-700 transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Attached file chip */}
      {attachedFile && (
        <div className="mb-2 flex items-center gap-2 p-2 bg-blue-50/90 rounded-xl text-xs text-blue-800 border border-blue-200 animate-in fade-in slide-in-from-bottom-1 duration-200">
          <Paperclip className="h-3.5 w-3.5 text-blue-600" />
          <span className="truncate max-w-[220px] font-semibold">{attachedFile.name}</span>
          <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-bold text-blue-700">
            {uploading ? (
              <><Loader2 className="h-3 w-3 animate-spin" /> Subiendo...</>
            ) : vectorizing ? (
              <><Loader2 className="h-3 w-3 animate-spin" /> Indexando...</>
            ) : documentRagReady ? (
              <><CheckCircle2 className="h-3 w-3 text-emerald-600" /> Listo</>
            ) : (
              <><Clock className="h-3 w-3" /> Pendiente</>
            )}
          </span>
          <button
            onClick={() => {
              setAttachedFile(null);
              setCloudflareFileKey(null);
              setLastUploadedDocUrl(null);
              setDocumentRagReady(false);
            }}
            className="ml-auto p-0.5 text-blue-400 hover:text-blue-600"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Modern Card Input Container (ChatGPT/Claude/Perplexity style) */}
      <div className="bg-white border border-slate-200/90 shadow-[0_2px_10px_rgba(0,0,0,0.04)] rounded-xl sm:rounded-2xl p-2 sm:p-2.5 flex flex-col gap-1 transition-all duration-200 focus-within:border-blue-500 focus-within:shadow-[0_4px_16px_rgba(59,130,246,0.1)] focus-within:ring-2 focus-within:ring-blue-100/70">
        
        {/* Textarea Input (Full Width for comfortable typing on all screens) */}
        <div className="w-full relative">
          <textarea
            ref={textareaRef}
            rows={1}
            maxLength={4000}
            value={message}
            disabled={isDocumentProcessing}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder={
              isDocumentProcessing
                ? "Procesando documento..."
                : isOpen
                ? `Consulta sobre "${docTitle}"...`
                : "Escribe tu consulta jurídica aquí..."
            }
            className="w-full bg-transparent border-0 outline-none text-slate-800 text-xs sm:text-sm placeholder-slate-400 resize-none min-h-[36px] sm:min-h-[38px] max-h-[120px] sm:max-h-[160px] py-0.5 px-1 font-normal leading-relaxed custom-scrollbar transition-all"
          />
        </div>

        {/* Action Toolbar */}
        <div className="flex items-center justify-between pt-1 border-t border-slate-100/80">
          {/* Left tools group */}
          <div className="flex items-center gap-1 sm:gap-1.5 flex-wrap">
            {/* Plus Button for Attach / Options Menu */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowAttachMenu(!showAttachMenu)}
                className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition-all flex items-center justify-center shrink-0 hover:scale-105 active:scale-95"
                title="Adjuntar o herramientas"
              >
                <Plus className="h-4 w-4" />
              </button>

              {/* Plus menu dropdown popup */}
              {showAttachMenu && (
                <div className="absolute bottom-full left-0 mb-3 w-60 bg-white rounded-2xl shadow-xl border border-slate-200 p-2 z-50 animate-in fade-in slide-in-from-bottom-2 flex flex-col gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAttachMenu(false);
                      if (storageUsage && storageUsage.percentage >= 100) {
                        toast.error("Límite de Almacenamiento Superado (5 GB). No es posible subir archivos.");
                        return;
                      }
                      fileInputRef.current?.click();
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-slate-100 text-xs font-semibold text-slate-700 transition-colors text-left"
                  >
                    <Paperclip className="h-4 w-4 text-blue-600" />
                    Adjuntar archivo legal
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAttachMenu(false);
                      setShowInstructions(true);
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-slate-100 text-xs font-semibold text-slate-700 transition-colors text-left"
                  >
                    <Info className="h-4 w-4 text-indigo-600" />
                    Instrucciones personalizadas
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAttachMenu(false);
                      setIsWebSearchEnabled(!isWebSearchEnabled);
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-slate-100 text-xs font-semibold text-slate-700 transition-colors text-left"
                  >
                    <Search className={`h-4 w-4 ${isWebSearchEnabled ? "text-emerald-600" : "text-slate-400"}`} />
                    Búsqueda Web {isWebSearchEnabled ? "(Activada)" : "(Desactivada)"}
                  </button>
                </div>
              )}
            </div>

            {/* Playbook/Skill Badge */}
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setShowSkillsMenu(!showSkillsMenu);
                  setShowFeaturesMenu(false);
                }}
                className={`h-7 sm:h-7.5 px-2 sm:px-2.5 rounded-full transition-all text-xs font-medium flex items-center gap-1 sm:gap-1.5 ${
                  selectedSkillId
                    ? "bg-indigo-50 text-indigo-700 border border-indigo-200"
                    : "bg-slate-100 hover:bg-slate-200/80 text-slate-600 border border-transparent"
                }`}
                title="Vincular Skill / Playbook"
              >
                <BookOpen className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-slate-500" />
                <span className="hidden sm:inline text-[10.5px] sm:text-[11px] font-semibold">Playbook</span>
              </button>
              {showSkillsMenu && (
                <div className="absolute bottom-full left-0 sm:left-auto sm:right-0 mb-3 w-64 bg-white rounded-2xl shadow-xl border border-slate-200 p-2.5 z-50 animate-in fade-in slide-in-from-bottom-2">
                  <div className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-100 mb-1 flex justify-between items-center">
                    Playbooks Disponibles
                    {loadingSkills && <Loader2 className="h-2.5 w-2.5 animate-spin" />}
                  </div>
                  <div className="max-h-48 overflow-y-auto custom-scrollbar flex flex-col gap-0.5">
                    <button
                      onClick={() => { setSelectedSkillId(null); setShowSkillsMenu(false); }}
                      className={`w-full text-left px-3 py-1.5 rounded-xl text-xs transition-colors flex items-center justify-between ${!selectedSkillId ? 'bg-indigo-50 text-indigo-700 font-bold' : 'hover:bg-slate-50 text-slate-600'}`}
                    >
                      Ninguno (Chat Estándar)
                      {!selectedSkillId && <Check className="h-3 w-3" />}
                    </button>
                    {skills.map((skill) => (
                      <button
                        key={skill.id}
                        onClick={() => { setSelectedSkillId(skill.id); setShowSkillsMenu(false); }}
                        className={`w-full text-left px-3 py-1.5 rounded-xl text-xs transition-colors flex flex-col gap-0.5 ${selectedSkillId === skill.id ? 'bg-indigo-50 text-indigo-700 font-bold' : 'hover:bg-slate-50 text-slate-600'}`}
                      >
                        <div className="flex items-center justify-between">
                          {skill.name}
                          {selectedSkillId === skill.id && <Check className="h-3 w-3" />}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Deep Research Mode Selector */}
            <button
              type="button"
              onClick={() => setIsResearchEnabled(!isResearchEnabled)}
              className={`h-7 sm:h-7.5 flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 rounded-full text-[10.5px] sm:text-[11px] font-extrabold transition-all shrink-0 select-none shadow-2xs ${
                isResearchEnabled
                  ? "bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-sm hover:from-indigo-700 hover:to-blue-700"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-800"
              }`}
              title={
                isResearchEnabled
                  ? "Modo Deep Research activo: Búsqueda jurídica multi-salto, sentencias hito y salvamentos de voto"
                  : "Modo Consulta Rápida: Respuesta jurídica directa"
              }
            >
              {isResearchEnabled ? (
                <>
                  <Sparkles className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-amber-300 animate-pulse" />
                  <span>Deep Research</span>
                </>
              ) : (
                <>
                  <Zap className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-slate-400" />
                  <span>Rápido</span>
                </>
              )}
            </button>
          </div>

          {/* Right Action: Send / Cancel Button */}
          <div className="flex items-center shrink-0 ml-1 sm:ml-2">
            {isCurrentlyRunning ? (
              <button
                type="button"
                onClick={() => conversationId && cancelRun(conversationId)}
                className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-red-600 text-white hover:bg-red-700 transition-all flex items-center justify-center shadow-2xs shrink-0"
                title="Cancelar"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => sendMessage()}
                disabled={(!message.trim() && !attachedFile) || isCurrentlyRunning || uploading || isDocumentProcessing}
                className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-slate-900 text-white hover:bg-blue-600 transition-all disabled:opacity-30 disabled:hover:bg-slate-900 flex items-center justify-center shadow-2xs shrink-0 hover:scale-105 active:scale-95"
                title="Enviar consulta"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M22 2L11 13" />
                  <path d="M22 2L15 22L11 13L2 9L22 2Z" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-full w-full overflow-hidden bg-white">
      {premiumStyles}

      {/* ── Sidebar Collapsed Icon Strip (Image 1 style) ── */}
      {!sidebarOpen && (
        <aside className="w-12 sm:w-14 flex-shrink-0 chat-sidebar hidden md:flex flex-col items-center py-3 sm:py-4 px-1 sm:px-2 border-r border-slate-200 bg-[#f8fafc] text-slate-600 gap-2.5 sm:gap-3 z-30 transition-all duration-300">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-1 rounded-xl hover:bg-slate-200/60 transition-all"
            title="Expandir sidebar"
          >
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-xs">
              <Bot className="h-4 w-4 text-white" />
            </div>
          </button>

          <div className="w-7 h-px bg-slate-200 my-0.5" />

          {/* Option 1: Nueva Consulta (SquarePen icon) */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => resetChat()}
                  className="p-2 rounded-xl hover:bg-slate-200/60 text-slate-600 hover:text-slate-900 transition-all"
                >
                  <SquarePen className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Nueva consulta</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Option 2: Buscar (Search icon) */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => {
                    setSidebarOpen(true);
                    setTimeout(() => sidebarSearchRef.current?.focus(), 150);
                  }}
                  className="p-2 rounded-xl hover:bg-slate-200/60 text-slate-600 hover:text-slate-900 transition-all"
                >
                  <Search className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Buscar consultas</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Option 3: Filtros / Fijados (Pin icon) */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => {
                    setSidebarOpen(true);
                    setShowOnlyPinned(!showOnlyPinned);
                  }}
                  className={`p-2 rounded-xl transition-all ${
                    showOnlyPinned
                      ? "bg-amber-100 text-amber-700 border border-amber-300"
                      : "hover:bg-slate-200/60 text-slate-600 hover:text-slate-900"
                  }`}
                >
                  <Pin className={`h-4 w-4 ${showOnlyPinned ? "fill-amber-500 text-amber-500" : ""}`} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">
                {showOnlyPinned ? "Ver todas las consultas" : "Ver solo consultas fijadas"}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Option 4: Historial (MessageSquare icon) */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="p-2 rounded-xl hover:bg-slate-200/60 text-slate-600 hover:text-slate-900 transition-all"
                >
                  <MessageSquare className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Historial de consultas</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </aside>
      )}

      {/* ── Mobile Backdrop for Sidebar Drawer ── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-xs z-40 md:hidden animate-in fade-in duration-200"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Sidebar Expanded ── */}
      <aside
        className={`chat-sidebar flex-shrink-0 flex flex-col transition-all duration-300 ${
          sidebarOpen
            ? "fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-white shadow-2xl md:relative md:w-60 lg:w-64 xl:w-72 md:shadow-none"
            : "w-0 hidden"
        } overflow-hidden`}
      >
        {/* Sidebar Header with 4 Iconic Option Buttons + Collapse */}
        <div className="p-3 border-b border-slate-200 flex flex-col gap-2.5 bg-[#f8fafc]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-xs">
                <Bot className="h-3.5 w-3.5 text-white" />
              </div>
              <span className="font-bold text-slate-800 text-xs">Consultas SarAI</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-1 rounded-lg hover:bg-slate-200/60 text-slate-500 hover:text-slate-800 transition-colors"
              title="Colapsar sidebar"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="9" y1="3" x2="9" y2="21" /></svg>
            </button>
          </div>

          {/* Quick Action Rail with 4 options */}
          <div className="grid grid-cols-4 gap-1 p-1 bg-slate-200/50 rounded-xl border border-slate-200/80">
            <button
              onClick={() => resetChat()}
              className="flex flex-col items-center justify-center p-1.5 rounded-lg hover:bg-white text-slate-600 hover:text-slate-900 transition-all hover:shadow-2xs"
              title="Nueva consulta"
            >
              <SquarePen className="h-4 w-4 mb-0.5 text-slate-600" />
              <span className="text-[9px] font-medium">Nueva</span>
            </button>
            <button
              onClick={() => sidebarSearchRef.current?.focus()}
              className="flex flex-col items-center justify-center p-1.5 rounded-lg hover:bg-white text-slate-600 hover:text-slate-900 transition-all hover:shadow-2xs"
              title="Buscar consultas"
            >
              <Search className="h-4 w-4 mb-0.5 text-slate-600" />
              <span className="text-[9px] font-medium">Buscar</span>
            </button>
            <button
              onClick={() => setShowOnlyPinned(!showOnlyPinned)}
              className={`flex flex-col items-center justify-center p-1.5 rounded-lg transition-all ${
                showOnlyPinned ? "bg-amber-100 text-amber-800 border border-amber-300" : "hover:bg-white text-slate-600 hover:text-slate-900 hover:shadow-2xs"
              }`}
              title={showOnlyPinned ? "Ver todas las consultas" : "Filtrar fijadas"}
            >
              <Pin className={`h-4 w-4 mb-0.5 ${showOnlyPinned ? "fill-amber-500 text-amber-500" : "text-slate-600"}`} />
              <span className="text-[9px] font-medium">Fijados</span>
            </button>
            <button
              onClick={() => sidebarSearchRef.current?.focus()}
              className="flex flex-col items-center justify-center p-1.5 rounded-lg hover:bg-white text-slate-600 hover:text-slate-900 transition-all hover:shadow-2xs"
              title="Historial"
            >
              <MessageSquare className="h-4 w-4 mb-0.5 text-slate-600" />
              <span className="text-[9px] font-medium">Chats</span>
            </button>
          </div>
        </div>

        {/* Search input in sidebar */}
        <div className="px-3 py-2.5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input
              ref={sidebarSearchRef}
              type="text"
              placeholder="Buscar en el historial..."
              className="w-full bg-white border border-slate-200 rounded-xl pl-9 pr-8 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all shadow-2xs"
              value={historySearchQuery}
              onChange={(e) => setHistorySearchQuery(e.target.value)}
            />
            {historySearchQuery && (
              <button
                onClick={() => setHistorySearchQuery("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-slate-100 text-slate-400 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>

        {/* Grouped History List */}
        <div className="flex-1 overflow-y-auto px-2 py-2 space-y-3 custom-scrollbar">
          {groupHistoryByDate(filteredHistory).map((group) => (
            <div key={group.label}>
              <div className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider px-2 mb-1.5 flex items-center gap-1">
                <span>{group.label}</span>
                <span className="text-[10px] font-normal text-slate-400">({group.items.length})</span>
              </div>
              {group.items.map((item) => (
                <div
                  key={item.id}
                  className={`chat-history-item group flex items-center justify-between rounded-lg sm:rounded-xl px-2.5 py-1.5 sm:py-2 mb-0.5 cursor-pointer transition-all ${
                    conversationId === item.id ? "active bg-blue-50/80 text-blue-900 border-l-2 border-blue-600 shadow-2xs font-medium" : "hover:bg-slate-100 text-slate-700"
                  }`}
                  onClick={() => handleHistoryClick(item)}
                >
                  <div className="flex-1 min-w-0 mr-2">
                    <div className={`text-[12px] sm:text-[13px] leading-snug truncate ${conversationId === item.id ? "font-bold text-blue-950" : "font-semibold text-slate-800"}`}>{item.title}</div>
                    <div className="text-[10px] sm:text-[10.5px] text-slate-500 mt-0.5 flex items-center gap-1">
                      <Clock className="h-3 w-3 text-slate-400" />
                      <span>{new Date(item.date).toLocaleDateString("es-CO", { day: "numeric", month: "short" })}</span>
                      <span>•</span>
                      <span>{item.messages.length} msgs</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5">
                    <button
                      onClick={(e) => togglePinChat(item.id, e)}
                      className={`p-1.5 rounded-lg hover:bg-slate-200/70 transition-all ${
                        pinnedChatIds.includes(item.id)
                          ? "text-amber-500 opacity-100"
                          : "opacity-0 group-hover:opacity-100 text-slate-400 hover:text-amber-500"
                      }`}
                      title={pinnedChatIds.includes(item.id) ? "Desfijar consulta" : "Fijar consulta"}
                    >
                      <Pin className={`h-3.5 w-3.5 ${pinnedChatIds.includes(item.id) ? "fill-amber-500 text-amber-500" : ""}`} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setPendingDeleteHistoryItem(item);
                        setShowDeleteHistoryModal(true);
                      }}
                      className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-slate-200/70 text-slate-400 hover:text-red-500 transition-all"
                      title="Eliminar consulta"
                    >
                      <Trash className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ))}
          {filteredHistory.length === 0 && (
            <div className="text-center text-slate-400 text-xs py-10 px-4">
              {historySearchQuery ? "No se encontraron consultas para tu búsqueda" : "Sin historial aún"}
            </div>
          )}
        </div>

        {/* Bottom Storage Usage */}
        {storageUsage && (
          <div className="p-3 border-t border-slate-200 text-[11px] text-slate-500 bg-[#f8fafc]">
            <div className="flex justify-between mb-1">
              <span>Almacenamiento</span>
              <span className="font-semibold text-slate-700">{storageUsage.usedFormatted} / {storageUsage.limitFormatted}</span>
            </div>
            <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${
                  storageUsage.percentage > 85 ? "bg-red-500" : storageUsage.percentage > 60 ? "bg-amber-500" : "bg-blue-600"
                }`}
                style={{ width: `${Math.min(100, storageUsage.percentage)}%` }}
              />
            </div>
          </div>
        )}
      </aside>

      {/* ── Main Panel ── */}
      <main className="flex-1 flex flex-col min-w-0 relative h-full overflow-hidden bg-slate-50/50">
        {/* Top bar (sidebar toggle + title) */}
        <div className="flex-shrink-0 flex items-center justify-between px-3 sm:px-4 py-1.5 sm:py-2 border-b border-slate-200/80 bg-white z-20 shadow-2xs">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {isMobile && (
              <button
                onClick={() => navigate("/dashboard/agents/mobile")}
                className="p-1 sm:p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors shrink-0"
                title="Volver a agentes"
              >
                <ArrowLeft className="h-4.5 w-4.5" />
              </button>
            )}
            {!sidebarOpen && (
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-1 sm:p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors shrink-0"
                title="Abrir historial"
              >
                <History className="h-4 w-4" />
              </button>
            )}
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-xs shrink-0">
                <Bot className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
              </div>
              <span className="font-bold text-slate-800 text-xs sm:text-sm truncate">
                SarAI — Asistente Jurídico
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => resetChat()}
              className="p-1 sm:p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-blue-600 transition-colors"
              title="Nueva consulta"
            >
              <SquarePen className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Optional Document Actions Panel */}
        {isOpen && !isMobile && (
          <div className="flex-shrink-0 border-b border-slate-100 bg-slate-50/50 py-1.5">
            <div className="max-w-5xl mx-auto w-full px-3 md:px-6">
              <DocumentActionsPanel
                onAction={handleDocumentAction}
                onToggleContext={setIsFocusedContext}
                isFocusedContext={isFocusedContext}
                isRagReady={documentRagReady}
                isVectorizing={vectorizing}
                isUploading={uploading}
                requiresIndexing={requiresIndexing}
              />
            </div>
          </div>
        )}

        {/* SCROLLABLE CONTENT AREA */}
        <div className="flex-1 min-h-0 bg-white relative flex flex-col overflow-hidden">
          {messages.length === 0 ? (
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              <div className="flex flex-col items-center justify-center min-h-full py-4 sm:py-6 md:py-8 px-3 sm:px-4 md:px-6 max-w-3xl mx-auto">
                <div className="mb-4 sm:mb-6 text-center max-w-xl">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 rounded-xl sm:rounded-2xl bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 flex items-center justify-center shadow-md shadow-blue-500/20 animate-in zoom-in duration-300">
                    <Bot className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  </div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-extrabold text-slate-800 mb-1 tracking-tight">
                    SarAI — Inteligencia Jurídica Colombiana
                  </h2>
                  <p className="text-slate-500 text-xs sm:text-sm leading-relaxed">
                    Analiza contratos, investiga jurisprudencia y genera documentos jurídicos con IA especializada en derecho colombiano.
                  </p>
                </div>

                {/* Examples Grid */}
                <div className="w-full">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <div className="h-px flex-1 bg-slate-100"></div>
                    <h3 className="text-[9.5px] sm:text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Sugerencias de consulta</h3>
                    <div className="h-px flex-1 bg-slate-100"></div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-2.5">
                    {[
                      "¿Puede un empleador despedir sin justa causa?",
                      "Analiza riesgos de contrato de arrendamiento",
                      "Jurisprudencia sobre estabilidad laboral reforzada",
                      "Redacta un derecho de petición contra una EPS",
                    ].map((example, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setMessage(example);
                          textareaRef.current?.focus();
                        }}
                        className="text-left px-3 sm:px-3.5 py-2 sm:py-2.5 rounded-xl border border-slate-100 bg-slate-50/60 hover:bg-white hover:border-blue-300 hover:shadow-2xs text-xs text-slate-700 transition-all flex items-center gap-2.5 group"
                      >
                        <div className="w-7 h-7 rounded-lg bg-white border border-slate-200/60 flex items-center justify-center shrink-0 shadow-2xs group-hover:bg-blue-50 group-hover:border-blue-200">
                          <MessageSquare className="h-3.5 w-3.5 text-slate-400 group-hover:text-blue-600 transition-colors" />
                        </div>
                        <span className="font-semibold text-slate-700 truncate">{example}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 relative flex flex-col min-h-0 bg-slate-50/40">
              {assistantIndices.length > 1 && (
                <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 z-30 hidden md:flex items-center">
                  <div className="pointer-events-auto flex flex-col items-center gap-1.5 py-2 px-1 rounded-full border border-slate-200 bg-white/95 shadow-md backdrop-blur-sm transition-all duration-200 hover:border-blue-300 hover:shadow-lg">
                    <button
                      onClick={() => navigateToAssistantOrdinal(assistantNavOrdinal - 1)}
                      disabled={assistantNavOrdinal <= 0}
                      className="w-6 h-6 flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-blue-600 rounded-full disabled:opacity-20 disabled:hover:bg-transparent disabled:hover:text-slate-500 transition-all cursor-pointer"
                      title="Anterior respuesta (Arriba)"
                    >
                      <ChevronUp className="h-3.5 w-3.5" />
                    </button>
                    <div className="flex flex-col items-center gap-1 my-0.5 max-h-48 overflow-y-auto px-0.5 py-0.5 custom-scrollbar">
                      {assistantIndices.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => navigateToAssistantOrdinal(i)}
                          className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors cursor-pointer group"
                          title={`Respuesta ${i + 1}`}
                          aria-label={`Respuesta ${i + 1}`}
                        >
                          <span
                            className={`rounded-full transition-all duration-200 ${
                              i === assistantNavOrdinal
                                ? "w-2.5 h-2.5 bg-blue-600 ring-4 ring-blue-100 shadow-xs scale-110"
                                : "w-2 h-2 bg-slate-300 group-hover:bg-slate-400 group-hover:scale-125"
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => navigateToAssistantOrdinal(assistantNavOrdinal + 1)}
                      disabled={assistantNavOrdinal >= assistantIndices.length - 1}
                      className="w-6 h-6 flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-blue-600 rounded-full disabled:opacity-20 disabled:hover:bg-transparent disabled:hover:text-slate-500 transition-all cursor-pointer"
                      title="Siguiente respuesta (Abajo)"
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              )}

              <div
                ref={scrollContainerRef}
                onScroll={handleContainerScroll}
                className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar relative flex flex-col w-full max-w-full min-w-0"
                onMouseUp={handleMouseUp}
                onTouchEnd={handleMouseUp}
              >
                <div className="flex-1 py-2 sm:py-3 md:py-4 px-2 sm:px-3 md:px-5 lg:px-6 w-full max-w-full min-w-0 overflow-x-hidden">
                  <div className={`mx-auto w-full max-w-4xl lg:max-w-4xl xl:max-w-5xl 2xl:max-w-6xl min-w-0 ${assistantIndices.length > 1 ? "md:pr-10 lg:pr-12" : ""} flex flex-col gap-2 sm:gap-2.5 md:gap-3`}>
                    {messages.map((msg, index) => {
                      const isAssistant = msg.role === "assistant";
                      const displayContent = msg.role === "user" ? msg.content.split("\n\n[Instrucciones para la IA:")[0] : msg.content;
                      const isLastAssistant = isAssistant && index === messages.length - 1;
                      const isStreaming = isLastAssistant && currentRun?.status === "streaming";
                      const messageId = index.toString();
                      const assistantOrd = assistantOrdinalForIndex.get(index);
                      const extractedFormObj = isAssistant ? extractClarificationFormFromContent(displayContent) : { body: displayContent, form: null };
                      const textWithoutForm = extractedFormObj.body;
                      const parsedBubble = isAssistant && !isStreaming ? parseQuestionsFromContent(textWithoutForm) : { questions: [] as string[], body: textWithoutForm };
                      const isLatestAssistant = isAssistant && (
                        index === messages.length - 1 || 
                        (index === messages.length - 2 && messages[messages.length - 1]?.role === "user")
                      );

                      // Normalize sources for this turn
                      const resolvedSources = isAssistant ? (() => {
                        const rawList = msg.sourceDetails?.length
                          ? msg.sourceDetails
                          : msg.sources?.length
                          ? msg.sources
                          : msg.metadata?.source_details?.length
                          ? msg.metadata.source_details
                          : msg.metadata?.sources?.length
                          ? msg.metadata.sources
                          : null;

                        if (rawList && rawList.length > 0) {
                          return rawList.map((sd: any, idx: number) => ({
                            name: typeof sd === 'string' ? sd : (sd.name || sd.title || `Fuente [${sd.index || idx + 1}]`),
                            url: sd.url || sd.storage_url || "",
                            raw_url: sd.raw_url || sd.url || sd.storage_url || "",
                            pageNumber: sd.pageNumber || sd.page,
                            excerpt: sd.referenced_content || sd.excerpt || sd.content || "",
                            highlight_text: sd.highlight_text || sd.referenced_content || sd.snippet || sd.excerpt || "",
                            article: sd.article,
                            index: sd.index !== undefined && sd.index !== null ? sd.index : (idx + 1),
                            is_canonical: sd.is_canonical,
                            vigencia: sd.vigencia,
                            retrieval_evidence: sd.retrieval_evidence,
                            part_type: sd.part_type,
                          }));
                        }

                        const parsed = parseSourcesFromContent(msg.content);
                        if (parsed?.items?.length) {
                          return parsed.items.map((it) => ({
                            name: it.name,
                            url: it.name.startsWith("http") ? it.name : "",
                            raw_url: it.name.startsWith("http") ? it.name : "",
                            pageNumber: it.pageNumber,
                            excerpt: it.citation,
                            highlight_text: it.highlight_text || it.name,
                            index: it.index,
                          }));
                        }

                        return (msg.sources || parsed?.sources || []).map((s: string, i: number) => {
                          const rawCitation = (msg.citations || parsed?.citations || [])[i] || "";
                          const pageMatch = (s + " " + rawCitation).match(/p(?:[áa]g(?:s|\.|\.s)?)\s*[:.]?\s*(\d+)/i);
                          return {
                            name: s,
                            url: s.startsWith("http") ? s : "",
                            raw_url: s.startsWith("http") ? s : "",
                            pageNumber: pageMatch ? parseInt(pageMatch[1], 10) : undefined,
                            excerpt: rawCitation || s,
                            highlight_text: s,
                            index: i + 1,
                          };
                        });
                      })() : [];

                      return (
                        <div
                          key={index}
                          data-message-index={index}
                          data-assistant-ordinal={msg.role === "assistant" ? assistantOrd : undefined}
                          ref={(el) => {
                            messageRowRefs.current[index] = el;
                            if (msg.role === "assistant" && assistantOrd !== undefined) assistantBubbleRefs.current[assistantOrd] = el;
                          }}
                          className={`chat-message-bubble group p-2.5 sm:p-3.5 md:p-4.5 rounded-xl sm:rounded-2xl border transition-all duration-200 relative w-full max-w-full min-w-0 overflow-hidden scroll-mt-6 ${msg.role === "user"
                            ? "msg-user bg-white border-slate-200/70 shadow-2xs hover:border-slate-300/80"
                            : "msg-assistant bg-white border-slate-200/80 shadow-xs before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[3px] before:bg-gradient-to-b before:from-blue-500 before:to-indigo-500 hover:border-blue-200/60 hover:shadow-sm"
                            }`}
                        >
                          {isAssistant && !isStreaming && (
                            <div className="absolute top-2 sm:top-2.5 right-2 sm:right-3 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-all duration-150 z-10 bg-white/95 backdrop-blur-sm border border-slate-200 shadow-sm rounded-xl p-0.5">
                              <button onClick={() => copyToClipboard(displayContent, messageId)} className="p-1 sm:p-1.5 rounded-lg hover:bg-slate-50 text-slate-400 hover:text-slate-700 transition-colors" title="Copiar">{copiedMessageId === messageId ? (<Check className="h-3.5 w-3.5 text-green-600" />) : (<Copy className="h-3.5 w-3.5" />)}</button>
                              <button onClick={() => handleDownloadPDF(displayContent, "consulta")} className="p-1 sm:p-1.5 rounded-lg hover:bg-slate-50 text-slate-400 hover:text-slate-700 transition-colors" title="PDF"><FileDown className="h-3.5 w-3.5" /></button>
                              <div className="h-3.5 w-px bg-slate-200 mx-0.5" />
                              <button className={`p-1 sm:p-1.5 rounded-lg hover:bg-slate-50 transition-colors ${msg.feedback === "thumbs_up" ? "text-green-600 bg-green-50" : "text-slate-400 hover:text-slate-700"}`} onClick={() => handleMessageFeedback(index, "thumbs_up")}><ThumbsUp className="h-3.5 w-3.5" /></button>
                              <button className={`p-1 sm:p-1.5 rounded-lg hover:bg-slate-50 transition-colors ${msg.feedback === "thumbs_down" ? "text-red-600 bg-red-50" : "text-slate-400 hover:text-slate-700"}`} onClick={() => handleMessageFeedback(index, "thumbs_down")}><ThumbsDown className="h-3.5 w-3.5" /></button>
                            </div>
                          )}
                          {!isAssistant && !isStreaming && (
                            <div className="absolute top-2 sm:top-2.5 right-2 sm:right-3 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-all duration-150 z-10 bg-white/95 backdrop-blur-sm border border-slate-200 shadow-sm rounded-xl p-0.5">
                              <button
                                onClick={() => handleEditMessage(index, msg.content)}
                                disabled={isCurrentlyRunning}
                                className="p-1 sm:p-1.5 rounded-lg hover:bg-slate-50 text-slate-400 hover:text-slate-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Editar consulta"
                              >
                                <PenLine className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => copyToClipboard(displayContent, messageId)}
                                className="p-1 sm:p-1.5 rounded-lg hover:bg-slate-50 text-slate-400 hover:text-slate-700 transition-colors"
                                title="Copiar"
                              >
                                {copiedMessageId === messageId ? (<Check className="h-3.5 w-3.5 text-green-600" />) : (<Copy className="h-3.5 w-3.5" />)}
                              </button>
                            </div>
                          )}

                          <div className="flex gap-2 sm:gap-3 md:gap-3.5 w-full min-w-0 max-w-full">
                            <div className="flex-shrink-0 mt-0.5">
                              {msg.role === "assistant" ? (
                                <div className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-lg sm:rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-xs"><Bot className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" /></div>
                              ) : (
                                <div className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-lg sm:rounded-xl bg-slate-100 flex items-center justify-center border border-slate-200/80 shadow-2xs"><User className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-slate-600" /></div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0 max-w-full overflow-hidden">
                              <div className="text-[10px] sm:text-[10.5px] font-bold text-slate-400 uppercase tracking-wider mb-1 sm:mb-1.5 flex items-center gap-1.5">
                                {msg.role === "assistant" ? "SarAI" : "Tú"}
                                <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                                <span className="font-medium lowercase tracking-normal text-slate-400/90">{formatTimestamp(msg.timestamp)}</span>
                                {isAssistant && (msg.metadata?.execution_time_seconds || msg.metadata?.executionTime) && (
                                  <>
                                    <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                                    <span className="inline-flex items-center gap-1 font-semibold text-slate-500 bg-slate-100/90 px-1.5 py-0.2 rounded-full text-[9.5px] border border-slate-200/60 lowercase tracking-normal">
                                      <Clock className="w-2.5 h-2.5 text-slate-400" />
                                      {Number(msg.metadata?.execution_time_seconds ?? msg.metadata?.executionTime).toFixed(1)} s
                                    </span>
                                  </>
                                )}
                              </div>
                              {/* Pipeline Traceability block inside assistant message */}
                              {isAssistant && (() => {
                                let rawSteps = msg.metadata?.traceSteps || (msg as any).traceSteps;

                                if (!rawSteps && conversationId) {
                                  try {
                                    const cached = localStorage.getItem(`chat_trace_${conversationId}_${index}`);
                                    if (cached) rawSteps = JSON.parse(cached);
                                  } catch (e) {}
                                }
                                
                                if ((!rawSteps || rawSteps.length === 0) && isLatestAssistant && traceSteps.length > 0) {
                                  rawSteps = traceSteps;
                                }

                                if (!rawSteps || rawSteps.length === 0) {
                                  if (resolvedSources && resolvedSources.length > 0) {
                                    rawSteps = [
                                      { message: "Búsqueda jurídica y recuperación de jurisprudencia", status: "done" },
                                      { message: `Análisis y cotejo de ${resolvedSources.length} fuentes legales citadas`, status: "done" },
                                      { message: "Generación de respuesta con sustento doctrinal y normativo", status: "done" }
                                    ];
                                  } else {
                                    rawSteps = [
                                      { message: "Búsqueda en base de datos normativa y jurisprudencial", status: "done" },
                                      { message: "Razonamiento jurídico y análisis doctrinal", status: "done" },
                                      { message: "Generación de respuesta jurídica fundamentada", status: "done" }
                                    ];
                                  }
                                }

                                const stepsToShow = rawSteps.map((s: TraceStep) => ({
                                  ...s,
                                  status: (!isStreaming && !isCurrentlyRunning && s.status === "running") ? "done" : s.status
                                }));

                                return (
                                  <div className="mb-2 sm:mb-2.5 animate-in fade-in slide-in-from-top-1 duration-200">
                                    <TraceProgress 
                                      steps={stepsToShow} 
                                      defaultExpanded={false} 
                                      executionTime={msg.metadata?.execution_time_seconds ?? msg.metadata?.executionTime}
                                    />
                                  </div>
                                );
                              })()}
                              {((isStreaming || isCurrentlyRunning) && isLatestAssistant) && (
                                <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-blue-600 animate-pulse">
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  <span>SarAI está redactando el dictamen jurídico fundamentado...</span>
                                </div>
                              )}
                              <div className={`prose prose-slate max-w-none text-slate-800 ${isStreaming && msg.content ? "typing-cursor" : ""} [overflow-wrap:anywhere] break-words text-[13px] sm:text-[13.5px] md:text-[14px] leading-relaxed w-full max-w-full min-w-0 overflow-hidden prose-headings:font-bold prose-headings:text-slate-900 prose-headings:my-2 prose-headings:tracking-tight prose-h1:text-base sm:prose-h1:text-lg prose-h2:text-sm sm:prose-h2:text-base prose-h3:text-[13px] sm:prose-h3:text-sm prose-p:my-1.5 prose-p:leading-relaxed prose-li:my-0.5 prose-ul:my-1 prose-ul:pl-4 prose-ol:my-1 prose-ol:pl-4 prose-hr:my-2.5`}>
                                {editingMessageIndex === index ? (
                                  <div className="mt-2 animate-in fade-in slide-in-from-top-1">
                                    <Textarea autoFocus value={editingMessageContent} onChange={(e) => setEditingMessageContent(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitMessageEdit(); } else if (e.key === "Escape") { cancelEdit(); } }} className="w-full bg-white border-slate-200 focus:ring-blue-500 focus:border-blue-500 rounded-xl text-sm min-h-[100px]" />
                                    <div className="flex justify-end gap-2 mt-2">
                                      <button onClick={cancelEdit} className="px-3 py-1.5 text-[10px] font-bold text-slate-500 hover:text-slate-700 uppercase tracking-wider">Cancelar</button>
                                      <button onClick={submitMessageEdit} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-[10px] font-bold uppercase tracking-wider hover:bg-blue-700 transition-colors shadow-sm">Guardar y re-enviar</button>
                                    </div>
                                  </div>
                                ) : (
                                  <MessageContent content={parsedBubble.body} highlights={highlights[index]} sources={resolvedSources} />
                                )}
                              </div>

                              {isAssistant && resolvedSources.length > 0 && (
                                <SourcesList
                                  sourceDetails={resolvedSources}
                                />
                              )}

                              {(() => {
                                const clarificationForm = msg.metadata?.clarification_form || extractedFormObj.form;
                                if (clarificationForm) {
                                  return (
                                    <ClarificationFormCard
                                      form={clarificationForm}
                                      onSubmit={(selectedQuery) => sendMessage(selectedQuery)}
                                      disabled={isCurrentlyRunning}
                                    />
                                  );
                                }

                                const extractedQuestions = parsedBubble.questions;
                                let allQuestions = Array.from(
                                  new Set([...(msg.relatedQuestions || []), ...extractedQuestions])
                                ).slice(0, 3);

                                // Fallback: If no questions were returned, generate contextual questions from sources/topics
                                if (allQuestions.length === 0 && !isStreaming && resolvedSources.length > 0) {
                                  const primeSource = resolvedSources[0]?.name?.replace(/\[\d+\]/, '').trim();
                                  allQuestions = [
                                    "¿Qué requisitos de procedibilidad exige la jurisprudencia en este caso?",
                                    primeSource ? `¿Cuáles son los precedentes vinculantes derivados de ${primeSource}?` : "¿Cuál es el término legal o caducidad aplicable a esta materia?",
                                    "¿Qué excepciones o medios de defensa existen frente a este problema jurídico?"
                                  ].slice(0, 3);
                                }

                                if (allQuestions.length > 0) {
                                  return <RelatedQuestions questions={allQuestions} onSelect={handleSuggestionSelect} />;
                                }
                                return null;
                              })()}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {isCurrentlyRunning && messages[messages.length - 1]?.role === "user" && (
                      <div className="w-full my-3 space-y-3 animate-in fade-in duration-200">
                        <TraceProgress defaultExpanded={false} steps={traceSteps.length > 0 ? traceSteps : [{ emoji: "🔍", message: "Iniciando análisis y consulta jurídica...", status: "running" }]} />
                        <div className="p-3.5 rounded-2xl bg-gradient-to-r from-blue-50/70 via-indigo-50/40 to-white border border-blue-100 flex items-center justify-between shadow-2xs">
                          <div className="flex items-center gap-3">
                            <div className="w-7 h-7 rounded-lg bg-blue-600 text-white flex items-center justify-center shadow-xs">
                              <Bot className="w-4 h-4 animate-bounce" />
                            </div>
                            <div>
                              <span className="text-xs font-bold text-slate-900 block">
                                SarAI está formulando el dictamen jurídico...
                              </span>
                              <span className="text-[10.5px] text-slate-500 font-medium">
                                Procesando fuentes normativas y construyendo subsunción en tiempo real.
                              </span>
                            </div>
                          </div>
                          <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-100/70 text-blue-700 text-[10px] font-bold">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Redactando respuesta
                          </span>
                        </div>
                      </div>
                    )}
                    {isCurrentlyRunning && traceSteps.length === 0 && (
                      <div className="p-6 md:p-8 bg-white border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.01)] rounded-3xl flex items-center justify-center">
                        <LoadingSpinner />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* FIXED ANALYSIS PANEL */}
        {!loading && currentMetadata && showAnalysis && (
          <div className="flex-shrink-0 border-t border-slate-100 bg-slate-50/80 z-20">
            <div className="max-w-5xl mx-auto w-full px-4 md:px-8 py-3">
              <button onClick={() => setShowAnalysis(false)} className="w-full flex items-center justify-between pb-2 text-xs font-bold text-slate-500 uppercase tracking-wider hover:text-slate-700 transition-colors">
                <div className="flex items-center gap-2"><Target className="h-3.5 w-3.5" />Métricas del Análisis</div>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="18 15 12 9 6 15" /></svg>
              </button>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                <div className="bg-white rounded-lg p-2.5 border border-slate-200"><span className="text-[10px] text-blue-600 font-bold uppercase block">Confianza</span><span className="text-base font-black text-slate-800">{Math.round((currentMetadata.confidence || 0) * 100)}%</span></div>
                <div className="bg-white rounded-lg p-2.5 border border-slate-200"><span className="text-[10px] text-green-600 font-bold uppercase block">Fuentes</span><span className="text-base font-black text-slate-800">{currentMetadata.sources || 0}</span></div>
                <div className="bg-white rounded-lg p-2.5 border border-slate-200"><span className="text-[10px] text-purple-600 font-bold uppercase block">Cobertura</span><span className="text-base font-black text-slate-800">{currentMetadata.coverage || 0}%</span></div>
                <div className="bg-white rounded-lg p-2.5 border border-slate-200"><span className="text-[10px] text-amber-600 font-bold uppercase block">Valoración</span><div className="flex gap-0.5 mt-0.5">{[...Array(5)].map((_, i) => (<Star key={i} size={13} className={i < (currentMetadata.stars || 0) ? "fill-yellow-400 text-yellow-400" : "text-slate-300"} />))}</div></div>
                <div className="bg-white rounded-lg p-2.5 border border-slate-200"><span className="text-[10px] text-indigo-600 font-bold uppercase block">Tiempo</span><span className="text-base font-black text-slate-800">{(currentMetadata.execution_time_seconds || currentMetadata.executionTime) ? `${Number(currentMetadata.execution_time_seconds || currentMetadata.executionTime).toFixed(1)}s` : "-"}</span></div>
              </div>
            </div>
          </div>
        )}

        {/* FIXED BOTTOM SEARCH INPUT CONTAINER & DISCLAIMER */}
        <div className="flex-shrink-0 bg-white/95 backdrop-blur-md border-t border-slate-200/80 px-2.5 sm:px-4 pt-1 sm:pt-1.5 pb-[calc(3.5rem+env(safe-area-inset-bottom,0px))] md:pb-2 z-20">
          <div className="max-w-4xl lg:max-w-4xl xl:max-w-5xl 2xl:max-w-6xl mx-auto w-full flex flex-col gap-1">
            {renderSearchInput()}
            <p className="text-[9.5px] sm:text-[10px] text-slate-400 text-center font-medium select-none tracking-tight">
              SarAI puede cometer errores. Verifica la información legal importante.
            </p>
          </div>
        </div>

        {/* Global Floating Components */}
        {selection && (
          <SelectionToolbar
            rect={selection.rect}
            toolbarLeft={selection.toolbarLeft}
            toolbarTop={selection.toolbarTop}
            onSelectColor={(color) => saveHighlight(selection.text, color, selection.index)}
            onContinue={handleContinueWithSelection}
            onClose={() => setSelection(null)}
          />
        )}
        <input ref={fileInputRef} id="file-upload-global" type="file" className="hidden" onChange={async (e) => {
          if (e.target.files && e.target.files[0]) {
            await handleFileUpload(e.target.files[0]);
          }
        }} />
      </main>

      <Dialog open={showInstructions} onOpenChange={setShowInstructions}>
        <DialogContent>
          <DialogHeader><DialogTitle>Instrucciones para la IA</DialogTitle></DialogHeader>
          <Textarea placeholder="Agrega instrucciones específicas para la IA (por ejemplo: responde como un abogado colombiano, enfócate en derecho laboral, etc.)" value={instructions} onChange={(e) => setInstructions(e.target.value)} className="min-h-[100px] mt-2" />
          <DialogFooter><Button onClick={() => setShowInstructions(false)}>Guardar instrucciones</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {showDeleteHistoryModal && pendingDeleteHistoryItem && (
        <Dialog open={showDeleteHistoryModal} onOpenChange={setShowDeleteHistoryModal}>
          <DialogContent>
            <DialogHeader><DialogTitle>Confirmar eliminación</DialogTitle></DialogHeader>
            <p className="text-gray-700 mb-4">¿Está seguro de que desea eliminar la consulta sobre "<span className="font-semibold">{pendingDeleteHistoryItem.title}</span>"?</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowDeleteHistoryModal(false); setPendingDeleteHistoryItem(null); }}>Cancelar</Button>
              <Button variant="destructive" onClick={() => handleDeleteHistoryItem(pendingDeleteHistoryItem)}>Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
